import { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
import { api, getAuthUser, logout } from "@/lib/api";
import BrandHeader from "@/components/BrandHeader";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Badge } from "@/components/ui/badge";
import { Switch } from "@/components/ui/switch";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogDescription,
} from "@/components/ui/dialog";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  LogOut, RefreshCw, DollarSign, Fuel, CheckCircle2, XCircle,
  UserPlus, Trash2, Mail, MailCheck, MailX, Search, ShieldCheck, Loader2,
  Eye, Filter, KeyRound, CreditCard, Landmark, Calculator, Calendar,
  ChevronLeft, ChevronRight, FileDown, FileText, User as UserIcon,
  Image as ImageIcon, Shield, Activity, AlertTriangle,
} from "lucide-react";

export default function AdminDashboard() {
  const navigate = useNavigate();
  const user = getAuthUser();
  const [searchParams, setSearchParams] = useSearchParams();

  const [orders, setOrders] = useState([]);
  const [workers, setWorkers] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [editingCustomer, setEditingCustomer] = useState(null);
  const [historyCustomer, setHistoryCustomer] = useState(null);
  const [stats, setStats] = useState(null);
  const [zelleEnabled, setZelleEnabled] = useState(true);
  const [stripeEnabled, setStripeEnabled] = useState(true);
  const [savingPayments, setSavingPayments] = useState(false);
  const [emailReplyTo, setEmailReplyTo] = useState("");
  const [savingReplyTo, setSavingReplyTo] = useState(false);
  const [fuelTypes, setFuelTypes] = useState([]);
  const [gmail, setGmail] = useState({ credentials_configured: false, connected: false });
  const [loading, setLoading] = useState(false);
  const [scanning, setScanning] = useState(false);
  const [query, setQuery] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [dateRange, setDateRange] = useState("all"); // all | today | week | month
  const [customFrom, setCustomFrom] = useState(""); // YYYY-MM-DD
  const [customTo, setCustomTo] = useState("");   // YYYY-MM-DD
  const [page, setPage] = useState(1);
  const [detailOrder, setDetailOrder] = useState(null);
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [bulkDeleting, setBulkDeleting] = useState(false);
  const PAGE_SIZE = 10;

  const load = async () => {
    setLoading(true);
    try {
      const [o, w, s, c, g, cu] = await Promise.all([
        api.get("/admin/orders"),
        api.get("/admin/workers"),
        api.get("/admin/stats"),
        api.get("/admin/config"),
        api.get("/admin/gmail/status"),
        api.get("/admin/customers"),
      ]);
      setOrders(o.data);
      setWorkers(w.data);
      setStats(s.data);
      setZelleEnabled(c.data.zelle_enabled !== false);
      setStripeEnabled(c.data.stripe_enabled !== false);
      setEmailReplyTo(c.data.email_reply_to || "");
      setFuelTypes(c.data.fuel_types || []);
      setGmail(g.data);
      setCustomers(cu.data);
    } catch (e) {
      // 401 is handled by axios interceptor (redirects to login).
      // Log the rest so admin failures don't disappear silently.
      if (e?.response?.status !== 401) {
        console.error("AdminDashboard load failed:", e?.response?.data || e?.message || e);
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    const t = setInterval(load, 15000);
    return () => clearInterval(t);
  }, []);

  useEffect(() => {
    if (searchParams.get("gmail") === "connected") {
      toast.success("Gmail conectado correctamente");
      searchParams.delete("gmail");
      setSearchParams(searchParams, { replace: true });
    }
  }, [searchParams, setSearchParams]);

  const handleLogout = () => {
    logout();
    navigate("/login");
  };

  const changeStatus = async (orderId, newStatus) => {
    try {
      await api.patch(`/admin/orders/${orderId}/status`, { status: newStatus });
      toast.success(`Estado actualizado: ${statusLabel(newStatus)}`);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Error al cambiar estado");
    }
  };

  const saveReplyTo = async () => {
    const val = (emailReplyTo || "").trim();
    if (val && !val.includes("@")) return toast.error("Email inválido");
    setSavingReplyTo(true);
    try {
      await api.put("/admin/config", { email_reply_to: val });
      toast.success(val ? `Reply-To actualizado: ${val}` : "Reply-To desactivado");
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Error al guardar");
    } finally {
      setSavingReplyTo(false);
    }
  };

  const togglePayment = async (method, next) => {
    setSavingPayments(true);
    // Optimistic update
    if (method === "zelle") setZelleEnabled(next);
    else setStripeEnabled(next);
    try {
      const payload = method === "zelle" ? { zelle_enabled: next } : { stripe_enabled: next };
      const { data } = await api.put("/admin/config", payload);
      const bothOff = data && data.zelle_enabled === false && data.stripe_enabled === false;
      if (bothOff) {
        toast.warning("⚠ Ventas cerradas — ningún método de pago está activo", { duration: 6000 });
      } else {
        toast.success(
          `${method === "zelle" ? "Zelle" : "Tarjeta"} ${next ? "activado" : "desactivado"}`,
        );
      }
    } catch (e) {
      // Roll back
      if (method === "zelle") setZelleEnabled(!next);
      else setStripeEnabled(!next);
      toast.error(e?.response?.data?.detail || "Error al guardar");
    } finally {
      setSavingPayments(false);
    }
  };

  const confirmOrder = async (orderId) => {
    try {
      await api.post(`/admin/orders/${orderId}/confirm`);
      toast.success("Pago confirmado. Código generado.");
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Error");
    }
  };

  const cancelOrder = async (orderId) => {
    try {
      await api.post(`/admin/orders/${orderId}/cancel`);
      toast.success("Orden cancelada");
      load();
    } catch {
      toast.error("Error");
    }
  };

  const deleteOrder = async (orderId) => {
    try {
      await api.delete(`/admin/orders/${orderId}`);
      toast.success("Orden eliminada");
      setDetailOrder(null);
      load();
    } catch {
      toast.error("Error al eliminar");
    }
  };

  // Custom range wins over the preset if either date is set
  const customRangeActive = Boolean(customFrom || customTo);

  const parseYmd = (s, endOfDay = false) => {
    if (!s) return null;
    const [y, m, d] = s.split("-").map(Number);
    if (!y || !m || !d) return null;
    return endOfDay ? new Date(y, m - 1, d, 23, 59, 59, 999) : new Date(y, m - 1, d, 0, 0, 0, 0);
  };

  const customFromDate = parseYmd(customFrom);
  const customToDate = parseYmd(customTo, true);

  const dateRangeCutoff = (() => {
    if (customRangeActive) return null; // handled separately in matchesDateRange
    if (dateRange === "all") return null;
    const now = new Date();
    const cutoff = new Date(now);
    if (dateRange === "today") {
      cutoff.setHours(0, 0, 0, 0);
    } else if (dateRange === "week") {
      cutoff.setDate(now.getDate() - 7);
    } else if (dateRange === "month") {
      cutoff.setDate(now.getDate() - 30);
    }
    return cutoff;
  })();

  const matchesDateRange = (o) => {
    const d = o.created_at ? new Date(o.created_at) : null;
    if (customRangeActive) {
      if (!d) return false;
      if (customFromDate && d < customFromDate) return false;
      if (customToDate && d > customToDate) return false;
      return true;
    }
    if (!dateRangeCutoff) return true;
    return d && d >= dateRangeCutoff;
  };

  const clearCustomRange = () => {
    setCustomFrom("");
    setCustomTo("");
  };

  const matchesSearchAndStatus = (o) => {
    if (statusFilter !== "all" && o.status !== statusFilter) return false;
    if (!query.trim()) return true;
    const q = query.toLowerCase();
    return (
      o.name.toLowerCase().includes(q) ||
      o.email.toLowerCase().includes(q) ||
      o.phone.toLowerCase().includes(q) ||
      (o.code || "").toLowerCase().includes(q) ||
      o.id.includes(q)
    );
  };

  const filteredOrders = orders.filter((o) => matchesDateRange(o) && matchesSearchAndStatus(o));

  const totalPages = Math.max(1, Math.ceil(filteredOrders.length / PAGE_SIZE));
  const currentPage = Math.min(page, totalPages);
  const paginatedOrders = filteredOrders.slice(
    (currentPage - 1) * PAGE_SIZE,
    currentPage * PAGE_SIZE,
  );

  // Reset to page 1 whenever filters change
  useEffect(() => {
    setPage(1);
  }, [query, statusFilter, dateRange, customFrom, customTo]);

  // Selection helpers for bulk actions
  const toggleSelect = (id) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const clearSelection = () => setSelectedIds(new Set());

  const bulkDelete = async () => {
    const ids = Array.from(selectedIds);
    if (ids.length === 0) return;
    if (!window.confirm(`¿Eliminar ${ids.length} orden${ids.length === 1 ? "" : "es"}? Esta acción no se puede deshacer.`)) return;
    setBulkDeleting(true);
    try {
      const { data } = await api.post("/admin/orders/bulk-delete", { ids });
      toast.success(`${data.deleted} orden${data.deleted === 1 ? "" : "es"} eliminada${data.deleted === 1 ? "" : "s"}`);
      clearSelection();
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Error al eliminar órdenes");
    } finally {
      setBulkDeleting(false);
    }
  };

  // Accounting aggregates on date-filtered orders (revenue = confirmed + dispatched)
  const accountingOrders = orders.filter((o) => matchesDateRange(o) && (o.status === "confirmed" || o.status === "dispatched"));
  const zelleOrders = accountingOrders.filter((o) => o.payment_method === "zelle");
  const stripeOrders = accountingOrders.filter((o) => o.payment_method === "stripe");

  // Breakdown por tipo de combustible (solo órdenes confirmadas/despachadas del rango activo).
  const fuelBreakdown = (() => {
    const map = new Map();
    // seed with configured fuel types so all four cards are always visible
    for (const ft of fuelTypes || []) {
      if (ft && ft.key) map.set(ft.key, { key: ft.key, label: ft.label, count: 0, total: 0, liters: 0 });
    }
    // legacy bucket for orders without fuel_type
    map.set("_legacy", { key: "_legacy", label: "Gasolina B94 (histórico)", count: 0, total: 0, liters: 0 });
    for (const o of accountingOrders) {
      const key = o.fuel_type || "_legacy";
      const row = map.get(key) || { key, label: o.fuel_label || key, count: 0, total: 0, liters: 0 };
      row.count += 1;
      row.total += o.total || 0;
      row.liters += o.liters ?? o.gallons ?? 0;
      map.set(key, row);
    }
    // drop legacy bucket if nothing landed there (keeps UI clean when only new orders exist)
    if ((map.get("_legacy")?.count || 0) === 0) map.delete("_legacy");
    return Array.from(map.values()).filter((r) => r.count > 0 || r.key !== "_legacy");
  })();
  const sumTotal = (arr) => arr.reduce((s, o) => s + (o.total || 0), 0);
  const sumLiters = (arr) => arr.reduce((s, o) => s + (o.liters ?? o.gallons ?? 0), 0);
  const sumFees = (arr) => arr.reduce((s, o) => s + (o.fee || 0), 0);

  // Human-readable label for exports/subtitles
  const dateRangeLabel = (() => {
    if (customRangeActive) {
      return `${customFrom || "inicio"} → ${customTo || "hoy"}`;
    }
    return {
      all: "Todas las fechas",
      today: "Hoy",
      week: "Últimos 7 días",
      month: "Últimos 30 días",
    }[dateRange] || dateRange;
  })();

  const connectGmail = async () => {
    try {
      const { data } = await api.get("/admin/gmail/authorize");
      window.location.href = data.auth_url;
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Google Client no configurado");
    }
  };

  const disconnectGmail = async () => {
    await api.post("/admin/gmail/disconnect");
    toast.success("Gmail desconectado");
    load();
  };

  const scanZelle = async () => {
    setScanning(true);
    try {
      const { data } = await api.post("/admin/gmail/scan");
      if (data.error) {
        toast.error(data.error);
      } else {
        toast.success(`Escaneo: ${data.matched} orden(es) confirmada(s) de ${data.scanned} correo(s)`);
        load();
      }
    } catch (e) {
      toast.error("Error al escanear");
    } finally {
      setScanning(false);
    }
  };

  const verifyStripe = async (orderId) => {
    try {
      const { data } = await api.post(`/admin/orders/${orderId}/verify-stripe`);
      if (data.ok) {
        if (data.already) {
          toast.info(`La orden ya está ${data.already}`);
        } else {
          toast.success("Pago Stripe verificado y confirmado");
        }
        load();
      } else {
        toast.error(data.message || "Stripe aún no reporta el pago");
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Error al verificar con Stripe");
    }
  };

  const scanStripe = async () => {
    setScanning(true);
    try {
      const { data } = await api.post("/admin/stripe/scan");
      if (data.error) {
        toast.error(data.error);
      } else {
        toast.success(`Stripe: ${data.matched} confirmada(s) de ${data.scanned} pendientes`);
        load();
      }
    } catch (e) {
      toast.error("Error al escanear Stripe");
    } finally {
      setScanning(false);
    }
  };

  const saveCustomer = async (payload) => {
    if (!editingCustomer) return;
    try {
      await api.patch(`/admin/customers/${editingCustomer.id}`, payload);
      toast.success("Cliente actualizado");
      setEditingCustomer(null);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Error al guardar");
    }
  };

  const deleteCustomer = async (id) => {
    if (!window.confirm("¿Eliminar este cliente? Sus pedidos históricos se conservarán pero perderán el vínculo con la cuenta.")) return;
    try {
      await api.delete(`/admin/customers/${id}`);
      toast.success("Cliente eliminado");
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Error al eliminar");
    }
  };

  const resendCustomerEmail = async (orderId) => {
    try {
      const { data } = await api.post(`/admin/orders/${orderId}/resend-email`);
      toast.success(`Email reenviado a ${data.to} (${data.kind})`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Error al reenviar email");
    }
  };


  return (
    <div className="min-h-screen bg-zinc-950 text-white">
      <BrandHeader
        right={
          <div className="flex items-center gap-3">
            <div className="hidden sm:flex items-center gap-2 px-3 py-1.5 rounded-md bg-yellow-400/10 border border-yellow-400/20">
              <ShieldCheck className="w-3.5 h-3.5 text-yellow-400" />
              <span className="text-xs font-mono-display uppercase tracking-widest text-yellow-300">
                Admin · {user?.name}
              </span>
            </div>
            <Button
              variant="ghost"
              onClick={handleLogout}
              data-testid="logout-btn"
              className="text-zinc-400 hover:text-red-400 hover:bg-red-500/10"
            >
              <LogOut className="w-4 h-4 mr-1.5" /> Salir
            </Button>
          </div>
        }
      />

      <main className="max-w-7xl mx-auto px-4 md:px-8 py-8">
        {/* Stats */}
        <div className="grid grid-cols-2 md:grid-cols-5 gap-3 md:gap-4 mb-8">
          <StatCard label="Pendientes" value={stats?.pending ?? "-"} icon={Loader2} color="text-amber-400" testid="stat-pending" />
          <StatCard label="Confirmadas" value={stats?.confirmed ?? "-"} icon={CheckCircle2} color="text-emerald-400" testid="stat-confirmed" />
          <StatCard label="Despachadas" value={stats?.dispatched ?? "-"} icon={Fuel} color="text-blue-400" testid="stat-dispatched" />
          <StatCard label="Litros vendidos" value={stats?.total_liters_sold ?? stats?.total_gallons_sold ?? "-"} icon={Fuel} color="text-zinc-100" testid="stat-liters" />
          <StatCard label="Ingresos (USD)" value={stats ? `$${stats.revenue_usd}` : "-"} icon={DollarSign} color="text-yellow-400" testid="stat-revenue" />
        </div>

        <Tabs
          value={searchParams.get("tab") || "orders"}
          onValueChange={(v) => {
            const next = new URLSearchParams(searchParams);
            next.set("tab", v);
            setSearchParams(next, { replace: true });
          }}
          className="space-y-6"
        >
          <TabsList className="bg-zinc-900 border border-white/10 flex flex-wrap h-auto md:h-10 gap-1 md:gap-0 w-full md:w-auto">
            <TabsTrigger value="orders" data-testid="tab-orders" className="text-xs md:text-sm px-2.5 md:px-3 data-[state=active]:bg-yellow-400 data-[state=active]:text-black">
              Órdenes
            </TabsTrigger>
            <TabsTrigger value="dispatches" data-testid="tab-dispatches" className="text-xs md:text-sm px-2.5 md:px-3 data-[state=active]:bg-yellow-400 data-[state=active]:text-black">
              Registro Despachos
            </TabsTrigger>
            <TabsTrigger value="accounting" data-testid="tab-accounting" className="text-xs md:text-sm px-2.5 md:px-3 data-[state=active]:bg-yellow-400 data-[state=active]:text-black">
              Contabilidad
            </TabsTrigger>
            <TabsTrigger value="workers" data-testid="tab-workers" className="text-xs md:text-sm px-2.5 md:px-3 data-[state=active]:bg-yellow-400 data-[state=active]:text-black">
              Trabajadores
            </TabsTrigger>
            <TabsTrigger value="customers" data-testid="tab-customers" className="text-xs md:text-sm px-2.5 md:px-3 data-[state=active]:bg-yellow-400 data-[state=active]:text-black">
              Clientes
            </TabsTrigger>
            <TabsTrigger value="settings" data-testid="tab-settings" className="text-xs md:text-sm px-2.5 md:px-3 data-[state=active]:bg-yellow-400 data-[state=active]:text-black">
              Configuración
            </TabsTrigger>
            <TabsTrigger value="security" data-testid="tab-security" className="text-xs md:text-sm px-2.5 md:px-3 data-[state=active]:bg-yellow-400 data-[state=active]:text-black">
              Seguridad
            </TabsTrigger>
          </TabsList>

          {/* ORDERS */}
          <TabsContent value="orders" className="space-y-4">
            <div className="flex flex-wrap items-center justify-between gap-3">
              <h2 className="font-display font-bold text-2xl">
                Órdenes recientes
                <span className="ml-3 text-sm font-mono-display text-zinc-500 font-normal">
                  {filteredOrders.length} de {orders.length}
                </span>
              </h2>
              <div className="flex items-center gap-2">
                <Button
                  onClick={scanZelle}
                  disabled={scanning || (!gmail.connected && !gmail.imap_configured)}
                  data-testid="scan-zelle-btn"
                  className="bg-yellow-400 hover:bg-yellow-300 text-black font-bold"
                >
                  <Search className={`w-4 h-4 mr-1.5 ${scanning ? "animate-spin" : ""}`} />
                  Escanear Zelle
                </Button>
                <Button variant="outline" onClick={load} data-testid="refresh-btn" className="border-white/10 bg-transparent hover:bg-white/5">
                  <RefreshCw className="w-4 h-4" />
                </Button>
              </div>
            </div>

            {/* Filters */}
            <div className="flex flex-col sm:flex-row gap-3">
              <div className="relative flex-1">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-500" />
                <Input
                  placeholder="Buscar por nombre, email, teléfono, código..."
                  value={query}
                  onChange={(e) => setQuery(e.target.value)}
                  data-testid="order-search-input"
                  className="pl-9 bg-zinc-900 border-white/10 text-white placeholder:text-zinc-600 focus-visible:ring-yellow-400 focus-visible:border-yellow-400"
                />
              </div>
              <Select value={dateRange} onValueChange={setDateRange} disabled={customRangeActive}>
                <SelectTrigger
                  data-testid="date-range-select"
                  className="sm:w-52 bg-zinc-900 border-white/10 text-white focus:ring-yellow-400 disabled:opacity-50"
                >
                  <div className="flex items-center gap-2">
                    <Calendar className="w-3.5 h-3.5 text-zinc-500" />
                    <SelectValue placeholder="Rango de fechas" />
                  </div>
                </SelectTrigger>
                <SelectContent className="bg-zinc-900 border-white/10 text-white">
                  <SelectItem value="all" data-testid="date-all">Todas las fechas</SelectItem>
                  <SelectItem value="today" data-testid="date-today">Hoy</SelectItem>
                  <SelectItem value="week" data-testid="date-week">Últimos 7 días</SelectItem>
                  <SelectItem value="month" data-testid="date-month">Últimos 30 días</SelectItem>
                </SelectContent>
              </Select>
              <div className="flex items-center gap-1">
                <Input
                  type="date"
                  value={customFrom}
                  onChange={(e) => setCustomFrom(e.target.value)}
                  data-testid="date-from-input"
                  className="sm:w-40 bg-zinc-900 border-white/10 text-white focus-visible:ring-yellow-400 focus-visible:border-yellow-400"
                  aria-label="Desde"
                />
                <span className="text-zinc-500 text-xs">→</span>
                <Input
                  type="date"
                  value={customTo}
                  onChange={(e) => setCustomTo(e.target.value)}
                  data-testid="date-to-input"
                  className="sm:w-40 bg-zinc-900 border-white/10 text-white focus-visible:ring-yellow-400 focus-visible:border-yellow-400"
                  aria-label="Hasta"
                />
                {customRangeActive && (
                  <Button
                    size="sm"
                    variant="ghost"
                    onClick={clearCustomRange}
                    data-testid="clear-custom-range-btn"
                    className="text-zinc-400 hover:text-red-400 hover:bg-red-500/10 h-8 px-2"
                    title="Limpiar rango personalizado"
                  >
                    <XCircle className="w-4 h-4" />
                  </Button>
                )}
              </div>
              <Select value={statusFilter} onValueChange={setStatusFilter}>
                <SelectTrigger
                  data-testid="status-filter-select"
                  className="sm:w-56 bg-zinc-900 border-white/10 text-white focus:ring-yellow-400"
                >
                  <div className="flex items-center gap-2">
                    <Filter className="w-3.5 h-3.5 text-zinc-500" />
                    <SelectValue placeholder="Filtrar estado" />
                  </div>
                </SelectTrigger>
                <SelectContent className="bg-zinc-900 border-white/10 text-white">
                  <SelectItem value="all">Todos los estados</SelectItem>
                  <SelectItem value="pending">Pendientes</SelectItem>
                  <SelectItem value="confirmed">Confirmadas</SelectItem>
                  <SelectItem value="dispatched">Despachadas</SelectItem>
                  <SelectItem value="cancelled">Canceladas</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div className="border border-white/10 bg-zinc-900 rounded-xl overflow-x-auto">
              {selectedIds.size > 0 && (
                <div
                  className="flex items-center justify-between gap-3 px-4 py-2.5 border-b border-white/10 bg-yellow-400/10"
                  data-testid="bulk-actions-bar"
                >
                  <div className="text-sm font-mono-display text-yellow-200">
                    <span data-testid="bulk-selected-count" className="font-bold">{selectedIds.size}</span>
                    {" "}orden{selectedIds.size === 1 ? "" : "es"} seleccionada{selectedIds.size === 1 ? "" : "s"}
                  </div>
                  <div className="flex items-center gap-2">
                    <Button
                      size="sm"
                      variant="ghost"
                      onClick={clearSelection}
                      data-testid="bulk-clear-btn"
                      className="text-zinc-300 hover:text-white hover:bg-white/5 h-8"
                    >
                      Quitar selección
                    </Button>
                    <Button
                      size="sm"
                      onClick={bulkDelete}
                      disabled={bulkDeleting}
                      data-testid="bulk-delete-btn"
                      className="bg-red-500 hover:bg-red-400 text-white h-8"
                    >
                      <Trash2 className="w-3.5 h-3.5 mr-1.5" />
                      {bulkDeleting ? "Eliminando..." : "Eliminar seleccionadas"}
                    </Button>
                  </div>
                </div>
              )}
              <Table>
                <TableHeader>
                  <TableRow className="border-white/10 hover:bg-transparent">
                    <TableHead className="w-10 pl-4">
                      <input
                        type="checkbox"
                        aria-label="Seleccionar todas las órdenes de esta página"
                        data-testid="select-all-checkbox"
                        checked={paginatedOrders.length > 0 && paginatedOrders.every((o) => selectedIds.has(o.id))}
                        onChange={(e) => {
                          setSelectedIds((prev) => {
                            const next = new Set(prev);
                            if (e.target.checked) paginatedOrders.forEach((o) => next.add(o.id));
                            else paginatedOrders.forEach((o) => next.delete(o.id));
                            return next;
                          });
                        }}
                        className="h-4 w-4 accent-yellow-400 cursor-pointer"
                      />
                    </TableHead>
                    <TableHead className="text-zinc-500 uppercase text-xs tracking-widest">Cliente</TableHead>
                    <TableHead className="text-zinc-500 uppercase text-xs tracking-widest">Contacto</TableHead>
                    <TableHead className="text-zinc-500 uppercase text-xs tracking-widest">Combustible</TableHead>
                    <TableHead className="text-zinc-500 uppercase text-xs tracking-widest">Litros</TableHead>
                    <TableHead className="text-zinc-500 uppercase text-xs tracking-widest">Pago</TableHead>
                    <TableHead className="text-zinc-500 uppercase text-xs tracking-widest">Total</TableHead>
                    <TableHead className="text-zinc-500 uppercase text-xs tracking-widest">Estado</TableHead>
                    <TableHead className="text-zinc-500 uppercase text-xs tracking-widest">Código</TableHead>
                    <TableHead className="text-zinc-500 uppercase text-xs tracking-widest text-right">Acciones</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filteredOrders.length === 0 && (
                    <TableRow className="border-white/5">
                      <TableCell colSpan={10} className="text-center text-zinc-500 py-10">
                        {orders.length === 0 ? "Sin órdenes aún." : "Ninguna orden coincide con el filtro."}
                      </TableCell>
                    </TableRow>
                  )}
                  {paginatedOrders.map((o) => (
                    <TableRow
                      key={o.id}
                      className={`border-white/5 cursor-pointer hover:bg-white/[0.02] transition-colors ${
                        selectedIds.has(o.id) ? "bg-yellow-400/5" : ""
                      }`}
                      onClick={() => setDetailOrder(o)}
                      data-testid={`order-row-${o.id}`}
                    >
                      <TableCell className="pl-4" onClick={(e) => e.stopPropagation()}>
                        <input
                          type="checkbox"
                          aria-label={`Seleccionar orden ${o.code || o.id.slice(0, 8)}`}
                          data-testid={`select-order-${o.id}`}
                          checked={selectedIds.has(o.id)}
                          onChange={() => toggleSelect(o.id)}
                          className="h-4 w-4 accent-yellow-400 cursor-pointer"
                        />
                      </TableCell>
                      <TableCell className="font-medium">{o.name}</TableCell>
                      <TableCell className="text-zinc-400 text-sm">
                        <div>{o.email}</div>
                        <div className="text-xs">{o.phone}</div>
                      </TableCell>
                      <TableCell data-testid={`order-fuel-${o.id}`}>
                        <div className="text-sm text-yellow-200 font-medium">
                          {o.fuel_label || "Gasolina B94"}
                        </div>
                        {o.fuel_octane && (
                          <div className="text-[10px] uppercase tracking-widest text-zinc-500 font-mono-display">
                            {o.fuel_octane}
                          </div>
                        )}
                      </TableCell>
                      <TableCell className="font-mono-display">{o.liters ?? o.gallons}</TableCell>
                      <TableCell><PaymentBadge method={o.payment_method} /></TableCell>
                      <TableCell className="font-mono-display text-yellow-400 font-bold">
                        ${o.total.toFixed(2)}
                        {o.fuel_label && (
                          <div className="text-[10px] text-yellow-200/70 font-normal font-mono-display uppercase tracking-widest">
                            {o.fuel_label}
                          </div>
                        )}
                        {o.fee > 0 && (
                          <div className="text-[10px] text-zinc-500 font-normal">
                            (fee ${o.fee.toFixed(2)})
                          </div>
                        )}
                      </TableCell>
                      <TableCell><StatusBadge status={o.status} editable onChange={(s) => changeStatus(o.id, s)} /></TableCell>
                      <TableCell className="font-mono-display text-yellow-300 tracking-widest">
                        {o.code || "—"}
                        {o.zelle_duplicate_review && (
                          <div
                            className="mt-1 inline-flex items-center gap-1 rounded-md border border-red-500/50 bg-red-500/15 text-red-300 text-[10px] font-bold uppercase tracking-widest px-1.5 py-0.5"
                            title="Depósito Zelle duplicado — revisa el detalle antes de confirmar"
                            data-testid={`dup-badge-${o.id}`}
                          >
                            <AlertTriangle className="w-3 h-3" />
                            Revisar duplicado
                          </div>
                        )}
                      </TableCell>
                      <TableCell className="text-right" onClick={(e) => e.stopPropagation()}>
                        <div className="flex items-center justify-end gap-1.5">
                          {o.zelle_receipt_url && (
                            <a
                              href={`${process.env.REACT_APP_BACKEND_URL}${o.zelle_receipt_url}`}
                              target="_blank"
                              rel="noopener noreferrer"
                              data-testid={`view-receipt-${o.id}`}
                              title="Ver recibo Zelle enviado por el cliente"
                              className="inline-flex items-center justify-center h-8 w-8 rounded-md text-emerald-300 hover:text-emerald-200 hover:bg-emerald-500/10 transition-colors"
                            >
                              <ImageIcon className="w-3.5 h-3.5" />
                            </a>
                          )}
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={() => setDetailOrder(o)}
                            data-testid={`view-order-${o.id}`}
                            className="text-zinc-400 hover:text-yellow-400 hover:bg-white/5 h-8"
                          >
                            <Eye className="w-3.5 h-3.5" />
                          </Button>
                          {o.status === "pending" && (
                            <>
                              {o.payment_method === "stripe" && (
                                <Button
                                  size="sm"
                                  onClick={() => verifyStripe(o.id)}
                                  data-testid={`verify-stripe-${o.id}`}
                                  className="bg-indigo-500 hover:bg-indigo-400 text-white h-8"
                                  title="Consultar Stripe y confirmar si el pago se completó"
                                >
                                  <CreditCard className="w-3.5 h-3.5 mr-1" /> Verificar Stripe
                                </Button>
                              )}
                              <Button
                                size="sm"
                                onClick={() => confirmOrder(o.id)}
                                data-testid={`confirm-order-${o.id}`}
                                className="bg-emerald-500 hover:bg-emerald-400 text-white h-8"
                              >
                                <CheckCircle2 className="w-3.5 h-3.5 mr-1" /> Confirmar
                              </Button>
                              <Button
                                size="sm"
                                variant="ghost"
                                onClick={() => cancelOrder(o.id)}
                                data-testid={`cancel-order-${o.id}`}
                                className="text-red-400 hover:text-red-300 hover:bg-red-500/10 h-8"
                              >
                                <XCircle className="w-3.5 h-3.5" />
                              </Button>
                            </>
                          )}
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>

            {/* Pagination */}
            {filteredOrders.length > 0 && (
              <div className="flex items-center justify-between text-sm" data-testid="orders-pagination">
                <div className="text-zinc-500 font-mono-display text-xs tracking-widest">
                  Página {currentPage} de {totalPages} · {filteredOrders.length} orden{filteredOrders.length === 1 ? "" : "es"}
                </div>
                <div className="flex items-center gap-2">
                  <Button
                    size="sm"
                    variant="outline"
                    disabled={currentPage <= 1}
                    onClick={() => setPage((p) => Math.max(1, p - 1))}
                    data-testid="page-prev-btn"
                    className="border-white/10 bg-transparent text-zinc-300 hover:bg-white/5 h-8"
                  >
                    <ChevronLeft className="w-3.5 h-3.5 mr-1" /> Anterior
                  </Button>
                  <Button
                    size="sm"
                    variant="outline"
                    disabled={currentPage >= totalPages}
                    onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                    data-testid="page-next-btn"
                    className="border-white/10 bg-transparent text-zinc-300 hover:bg-white/5 h-8"
                  >
                    Siguiente <ChevronRight className="w-3.5 h-3.5 ml-1" />
                  </Button>
                </div>
              </div>
            )}
          </TabsContent>
          {/* ACCOUNTING */}
          <TabsContent value="accounting" className="space-y-4" data-testid="accounting-tab">
            <div className="flex flex-wrap items-center justify-between gap-3">
              <h2 className="font-display font-bold text-2xl">
                Contabilidad
                <span className="ml-3 text-sm font-mono-display text-zinc-500 font-normal">
                  {accountingOrders.length} pago{accountingOrders.length === 1 ? "" : "s"} · confirmados y despachados
                </span>
              </h2>
              <div className="flex flex-wrap items-center gap-2">
                <Select value={dateRange} onValueChange={setDateRange} disabled={customRangeActive}>
                  <SelectTrigger
                    data-testid="accounting-date-range-select"
                    className="w-52 bg-zinc-900 border-white/10 text-white focus:ring-yellow-400 disabled:opacity-50"
                  >
                    <div className="flex items-center gap-2">
                      <Calendar className="w-3.5 h-3.5 text-zinc-500" />
                      <SelectValue placeholder="Rango de fechas" />
                    </div>
                  </SelectTrigger>
                  <SelectContent className="bg-zinc-900 border-white/10 text-white">
                    <SelectItem value="all">Todas las fechas</SelectItem>
                    <SelectItem value="today">Hoy</SelectItem>
                    <SelectItem value="week">Últimos 7 días</SelectItem>
                    <SelectItem value="month">Últimos 30 días</SelectItem>
                  </SelectContent>
                </Select>
                <Input
                  type="date"
                  value={customFrom}
                  onChange={(e) => setCustomFrom(e.target.value)}
                  data-testid="accounting-date-from-input"
                  className="w-40 bg-zinc-900 border-white/10 text-white focus-visible:ring-yellow-400 focus-visible:border-yellow-400"
                  aria-label="Desde"
                />
                <span className="text-zinc-500 text-xs">→</span>
                <Input
                  type="date"
                  value={customTo}
                  onChange={(e) => setCustomTo(e.target.value)}
                  data-testid="accounting-date-to-input"
                  className="w-40 bg-zinc-900 border-white/10 text-white focus-visible:ring-yellow-400 focus-visible:border-yellow-400"
                  aria-label="Hasta"
                />
                {customRangeActive && (
                  <Button
                    size="sm"
                    variant="ghost"
                    onClick={clearCustomRange}
                    data-testid="accounting-clear-custom-range-btn"
                    className="text-zinc-400 hover:text-red-400 hover:bg-red-500/10 h-8 px-2"
                    title="Limpiar rango personalizado"
                  >
                    <XCircle className="w-4 h-4" />
                  </Button>
                )}
              </div>
            </div>

            {/* Totals cards */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <AccountingCard
                icon={Landmark}
                color="text-yellow-400"
                label="Zelle"
                count={zelleOrders.length}
                total={sumTotal(zelleOrders)}
                liters={sumLiters(zelleOrders)}
                fees={sumFees(zelleOrders)}
                testid="accounting-zelle-card"
              />
              <AccountingCard
                icon={CreditCard}
                color="text-indigo-300"
                label="Tarjeta (Stripe)"
                count={stripeOrders.length}
                total={sumTotal(stripeOrders)}
                liters={sumLiters(stripeOrders)}
                fees={sumFees(stripeOrders)}
                testid="accounting-stripe-card"
              />
              <AccountingCard
                icon={Calculator}
                color="text-emerald-400"
                label="Total general"
                count={accountingOrders.length}
                total={sumTotal(accountingOrders)}
                liters={sumLiters(accountingOrders)}
                fees={sumFees(accountingOrders)}
                highlight
                testid="accounting-total-card"
              />
            </div>

            {/* Breakdown por tipo de combustible */}
            {fuelBreakdown.length > 0 && (
              <div
                className="border border-white/10 bg-black/30 rounded-xl p-5"
                data-testid="accounting-fuel-breakdown"
              >
                <div className="flex items-center gap-2 mb-4">
                  <Fuel className="w-4 h-4 text-yellow-400" />
                  <span className="text-[11px] uppercase tracking-[0.3em] font-mono-display text-zinc-400">
                    Ventas por tipo de combustible
                  </span>
                </div>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                  {fuelBreakdown.map((f) => (
                    <FuelBreakdownCard key={f.key} row={f} />
                  ))}
                </div>
              </div>
            )}

            {/* Zelle table */}
            <AccountingSection
              title="Pagos por Zelle"
              icon={Landmark}
              color="text-yellow-400"
              orders={zelleOrders}
              emptyText="Sin pagos Zelle en este rango."
              testid="accounting-zelle-section"
              onRowClick={setDetailOrder}
              dateLabel={dateRangeLabel}
            />

            {/* Stripe table */}
            <AccountingSection
              title="Pagos por Tarjeta (Stripe)"
              icon={CreditCard}
              color="text-indigo-300"
              orders={stripeOrders}
              emptyText="Sin pagos con tarjeta en este rango."
              testid="accounting-stripe-section"
              onRowClick={setDetailOrder}
              showFee
              dateLabel={dateRangeLabel}
            />

            {/* Combined total */}
            <AccountingSection
              title="Total general (Zelle + Tarjeta)"
              icon={Calculator}
              color="text-emerald-400"
              orders={accountingOrders}
              emptyText="Sin pagos en este rango."
              testid="accounting-total-section"
              onRowClick={setDetailOrder}
              showFee
              dateLabel={dateRangeLabel}
            />
          </TabsContent>

          <TabsContent value="dispatches" className="space-y-4">
            <div className="flex flex-wrap items-center justify-between gap-3">
              <h2 className="font-display font-bold text-2xl">
                Registro de despachos
                <span className="ml-3 text-sm font-mono-display text-zinc-500 font-normal">
                  {orders.filter((o) => o.status === "dispatched").filter((o) => {
                    if (!customRangeActive && dateRange === "all") return true;
                    const d = o.dispatched_at ? new Date(o.dispatched_at) : null;
                    if (customRangeActive) {
                      if (!d) return false;
                      if (customFromDate && d < customFromDate) return false;
                      if (customToDate && d > customToDate) return false;
                      return true;
                    }
                    if (!dateRangeCutoff) return true;
                    return d && d >= dateRangeCutoff;
                  }).length} entregas
                </span>
              </h2>
              <div className="flex flex-wrap items-center gap-2">
                <Select value={dateRange} onValueChange={setDateRange} disabled={customRangeActive}>
                  <SelectTrigger data-testid="dispatch-date-range-select" className="w-52 bg-zinc-900 border-white/10 text-white focus:ring-yellow-400 disabled:opacity-50">
                    <div className="flex items-center gap-2">
                      <Calendar className="w-3.5 h-3.5 text-zinc-500" />
                      <SelectValue placeholder="Rango" />
                    </div>
                  </SelectTrigger>
                  <SelectContent className="bg-zinc-900 border-white/10 text-white">
                    <SelectItem value="all">Todas las fechas</SelectItem>
                    <SelectItem value="today">Hoy</SelectItem>
                    <SelectItem value="week">Últimos 7 días</SelectItem>
                    <SelectItem value="month">Últimos 30 días</SelectItem>
                  </SelectContent>
                </Select>
                <Input type="date" value={customFrom} onChange={(e) => setCustomFrom(e.target.value)} data-testid="dispatch-date-from-input" className="w-40 bg-zinc-900 border-white/10 text-white" aria-label="Desde" />
                <span className="text-zinc-500 text-xs">→</span>
                <Input type="date" value={customTo} onChange={(e) => setCustomTo(e.target.value)} data-testid="dispatch-date-to-input" className="w-40 bg-zinc-900 border-white/10 text-white" aria-label="Hasta" />
                <Button variant="outline" onClick={load} data-testid="dispatches-refresh-btn" className="border-white/10 bg-transparent hover:bg-white/5">
                  <RefreshCw className="w-4 h-4" />
                </Button>
              </div>
            </div>

            <div className="border border-white/10 bg-zinc-900 rounded-xl overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow className="border-white/10 hover:bg-transparent">
                    <TableHead className="text-zinc-500 uppercase text-xs tracking-widest">Fecha despacho</TableHead>
                    <TableHead className="text-zinc-500 uppercase text-xs tracking-widest">Cliente</TableHead>
                    <TableHead className="text-zinc-500 uppercase text-xs tracking-widest">Teléfono</TableHead>
                    <TableHead className="text-zinc-500 uppercase text-xs tracking-widest">Código</TableHead>
                    <TableHead className="text-zinc-500 uppercase text-xs tracking-widest">Combustible</TableHead>
                    <TableHead className="text-zinc-500 uppercase text-xs tracking-widest">Litros</TableHead>
                    <TableHead className="text-zinc-500 uppercase text-xs tracking-widest">Total</TableHead>
                    <TableHead className="text-zinc-500 uppercase text-xs tracking-widest">Pago</TableHead>
                    <TableHead className="text-zinc-500 uppercase text-xs tracking-widest">Trabajador</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {orders.filter((o) => o.status === "dispatched").length === 0 && (
                    <TableRow className="border-white/5">
                      <TableCell colSpan={9} className="text-center text-zinc-500 py-10">
                        Aún no hay despachos registrados.
                      </TableCell>
                    </TableRow>
                  )}
                  {orders
                    .filter((o) => o.status === "dispatched")
                    .filter((o) => {
                      if (!customRangeActive && dateRange === "all") return true;
                      const d = o.dispatched_at ? new Date(o.dispatched_at) : null;
                      if (customRangeActive) {
                        if (!d) return false;
                        if (customFromDate && d < customFromDate) return false;
                        if (customToDate && d > customToDate) return false;
                        return true;
                      }
                      if (!dateRangeCutoff) return true;
                      return d && d >= dateRangeCutoff;
                    })
                    .sort((a, b) => new Date(b.dispatched_at || 0) - new Date(a.dispatched_at || 0))
                    .map((o) => (
                      <TableRow
                        key={o.id}
                        className="border-white/5 cursor-pointer hover:bg-white/[0.02]"
                        onClick={() => setDetailOrder(o)}
                        data-testid={`dispatch-row-${o.id}`}
                      >
                        <TableCell className="text-sm text-zinc-300">
                          {o.dispatched_at
                            ? new Date(o.dispatched_at).toLocaleString("es-ES", {
                                day: "2-digit", month: "short", year: "numeric",
                                hour: "2-digit", minute: "2-digit",
                              })
                            : "—"}
                        </TableCell>
                        <TableCell className="font-medium">
                          {o.name}
                          <div className="text-xs text-zinc-500">{o.email}</div>
                        </TableCell>
                        <TableCell className="text-zinc-400 text-sm">{o.phone}</TableCell>
                        <TableCell className="font-mono-display font-bold text-yellow-300 tracking-widest">{o.code || "—"}</TableCell>
                        <TableCell>
                          <div className="text-sm text-yellow-200 font-medium">
                            {o.fuel_label || "Gasolina B94"}
                          </div>
                          {o.fuel_octane && (
                            <div className="text-[10px] uppercase tracking-widest text-zinc-500 font-mono-display">
                              {o.fuel_octane}
                            </div>
                          )}
                        </TableCell>
                        <TableCell className="font-mono-display">{o.liters ?? o.gallons}</TableCell>
                        <TableCell className="font-mono-display text-yellow-400 font-bold">${o.total.toFixed(2)}</TableCell>
                        <TableCell><PaymentBadge method={o.payment_method} /></TableCell>
                        <TableCell className="text-sm text-zinc-300">{o.dispatched_by || "—"}</TableCell>
                      </TableRow>
                    ))}
                </TableBody>
              </Table>
            </div>
          </TabsContent>

          {/* WORKERS */}
          <TabsContent value="workers" className="space-y-4">
            <div className="flex items-center justify-between">
              <h2 className="font-display font-bold text-2xl">Trabajadores</h2>
              <CreateWorkerDialog onCreated={load} />
            </div>

            <div className="border border-white/10 bg-zinc-900 rounded-xl overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow className="border-white/10 hover:bg-transparent">
                    <TableHead className="text-zinc-500 uppercase text-xs tracking-widest">Nombre</TableHead>
                    <TableHead className="text-zinc-500 uppercase text-xs tracking-widest">Email</TableHead>
                    <TableHead className="text-zinc-500 uppercase text-xs tracking-widest">Estado</TableHead>
                    <TableHead className="text-zinc-500 uppercase text-xs tracking-widest text-right">Acciones</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {workers.length === 0 && (
                    <TableRow className="border-white/5">
                      <TableCell colSpan={4} className="text-center text-zinc-500 py-10">
                        Sin trabajadores creados.
                      </TableCell>
                    </TableRow>
                  )}
                  {workers.map((w) => (
                    <TableRow key={w.id} className="border-white/5" data-testid={`worker-row-${w.id}`}>
                      <TableCell className="font-medium">{w.name}</TableCell>
                      <TableCell className="text-zinc-400">{w.email}</TableCell>
                      <TableCell>
                        <Badge className={w.active ? "bg-emerald-500/20 text-emerald-300 border-emerald-500/30" : "bg-zinc-700 text-zinc-300"}>
                          {w.active ? "Activo" : "Inactivo"}
                        </Badge>
                      </TableCell>
                      <TableCell className="text-right">
                        <div className="flex items-center justify-end gap-2">
                          <ChangePasswordDialog worker={w} />
                          <Button
                            size="sm"
                            variant="outline"
                            className="h-8 border-white/10 bg-transparent hover:bg-white/5"
                            onClick={async () => {
                              await api.patch(`/admin/workers/${w.id}/toggle`);
                              load();
                            }}
                            data-testid={`toggle-worker-${w.id}`}
                          >
                            {w.active ? "Desactivar" : "Activar"}
                          </Button>
                          <AlertDialog>
                            <AlertDialogTrigger asChild>
                              <Button size="sm" variant="ghost" className="text-red-400 hover:text-red-300 hover:bg-red-500/10 h-8" data-testid={`delete-worker-${w.id}`}>
                                <Trash2 className="w-3.5 h-3.5" />
                              </Button>
                            </AlertDialogTrigger>
                            <AlertDialogContent className="bg-zinc-900 border-white/10 text-white">
                              <AlertDialogHeader>
                                <AlertDialogTitle>¿Eliminar trabajador?</AlertDialogTitle>
                                <AlertDialogDescription className="text-zinc-400">
                                  Esta acción no se puede deshacer. {w.name} perderá acceso al panel.
                                </AlertDialogDescription>
                              </AlertDialogHeader>
                              <AlertDialogFooter>
                                <AlertDialogCancel className="bg-zinc-800 border-white/10 text-white hover:bg-zinc-700">Cancelar</AlertDialogCancel>
                                <AlertDialogAction
                                  className="bg-red-500 hover:bg-red-400 text-white"
                                  onClick={async () => {
                                    await api.delete(`/admin/workers/${w.id}`);
                                    toast.success("Trabajador eliminado");
                                    load();
                                  }}
                                >
                                  Eliminar
                                </AlertDialogAction>
                              </AlertDialogFooter>
                            </AlertDialogContent>
                          </AlertDialog>
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          </TabsContent>

          {/* CUSTOMERS */}
          <TabsContent value="customers" className="space-y-4" data-testid="customers-tab">
            <div className="flex items-center justify-between">
              <h2 className="font-display font-bold text-2xl">
                Clientes registrados
                <span className="ml-3 text-sm font-mono-display text-zinc-500 font-normal">
                  {customers.length} {customers.length === 1 ? "cuenta" : "cuentas"}
                </span>
              </h2>
            </div>
            <div className="border border-white/10 bg-zinc-900 rounded-xl overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow className="border-white/10 hover:bg-transparent">
                    <TableHead className="text-zinc-500 uppercase text-xs tracking-widest">Nombre</TableHead>
                    <TableHead className="text-zinc-500 uppercase text-xs tracking-widest">Email</TableHead>
                    <TableHead className="text-zinc-500 uppercase text-xs tracking-widest">Registrado</TableHead>
                    <TableHead className="text-zinc-500 uppercase text-xs tracking-widest">Pedidos</TableHead>
                    <TableHead className="text-zinc-500 uppercase text-xs tracking-widest">Litros</TableHead>
                    <TableHead className="text-zinc-500 uppercase text-xs tracking-widest">Gastado</TableHead>
                    <TableHead className="text-zinc-500 uppercase text-xs tracking-widest">Estado</TableHead>
                    <TableHead className="text-zinc-500 uppercase text-xs tracking-widest text-right">Acciones</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {customers.length === 0 && (
                    <TableRow className="border-white/5">
                      <TableCell colSpan={8} className="text-center text-zinc-500 py-10">
                        Aún no hay clientes registrados.
                      </TableCell>
                    </TableRow>
                  )}
                  {customers.map((c) => (
                    <TableRow key={c.id} className="border-white/5" data-testid={`customer-row-${c.id}`}>
                      <TableCell className="font-medium">{c.name}</TableCell>
                      <TableCell className="text-zinc-400 text-sm">{c.email}</TableCell>
                      <TableCell className="text-xs text-zinc-500">
                        {c.created_at ? new Date(c.created_at).toLocaleDateString("es-ES", { day: "2-digit", month: "short", year: "numeric" }) : "—"}
                      </TableCell>
                      <TableCell className="font-mono-display">{c.orders_count}</TableCell>
                      <TableCell className="font-mono-display">{(c.total_liters || 0).toFixed(2)}</TableCell>
                      <TableCell className="font-mono-display text-yellow-400 font-bold">${(c.total_spent || 0).toFixed(2)}</TableCell>
                      <TableCell>
                        <Badge className={c.active ? "bg-emerald-500/20 text-emerald-300 border-emerald-500/30" : "bg-zinc-700 text-zinc-400 border-zinc-600"}>
                          {c.active ? "Activo" : "Inactivo"}
                        </Badge>
                      </TableCell>
                      <TableCell className="text-right">
                        <div className="flex items-center justify-end gap-1.5">
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={() => setHistoryCustomer(c)}
                            data-testid={`view-customer-orders-${c.id}`}
                            className="text-zinc-400 hover:text-yellow-400 hover:bg-white/5 h-8"
                            title="Ver pedidos"
                          >
                            <Eye className="w-3.5 h-3.5" />
                          </Button>
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={() => setEditingCustomer(c)}
                            data-testid={`edit-customer-${c.id}`}
                            className="text-zinc-400 hover:text-yellow-400 hover:bg-white/5 h-8"
                            title="Editar cliente"
                          >
                            <UserIcon className="w-3.5 h-3.5" />
                          </Button>
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={() => deleteCustomer(c.id)}
                            data-testid={`delete-customer-${c.id}`}
                            className="text-red-400 hover:text-red-300 hover:bg-red-500/10 h-8"
                            title="Eliminar cliente"
                          >
                            <Trash2 className="w-3.5 h-3.5" />
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          </TabsContent>

          {/* SETTINGS */}
          <TabsContent value="settings" className="space-y-6">
            <div className="grid md:grid-cols-2 gap-4">
              {/* Fuel types editor */}
              <div className="border border-white/10 bg-zinc-900 rounded-xl p-6 md:col-span-2">
                <div className="text-[11px] uppercase tracking-[0.3em] text-zinc-500 font-mono-display mb-3">
                  Tipos de combustible (visibles al cliente en el landing)
                </div>
                <FuelTypesEditor
                  initial={fuelTypes}
                  onSaved={(list) => { setFuelTypes(list); load(); }}
                />
              </div>
              {/* Reply-To email */}
              <div className="border border-white/10 bg-zinc-900 rounded-xl p-6 md:col-span-2">
                <div className="text-[11px] uppercase tracking-[0.3em] text-zinc-500 font-mono-display mb-2">
                  Reply-To de emails al cliente
                </div>
                <div className="text-xs text-zinc-400 mb-3 leading-relaxed">
                  Cuando un cliente responda a un email automático, la respuesta irá a este correo.
                  Déjalo vacío para que las respuestas vayan al remitente SMTP por defecto.
                </div>
                <div className="flex items-center gap-2">
                  <Input
                    type="email"
                    value={emailReplyTo}
                    onChange={(e) => setEmailReplyTo(e.target.value)}
                    placeholder="soporte@cupetb94.com"
                    data-testid="reply-to-input"
                    className="bg-zinc-950 border-white/10 text-white focus-visible:ring-yellow-400"
                  />
                  <Button
                    onClick={saveReplyTo}
                    disabled={savingReplyTo}
                    data-testid="save-reply-to-btn"
                    className="bg-yellow-400 hover:bg-yellow-300 text-black font-bold shrink-0"
                  >
                    Guardar
                  </Button>
                </div>
              </div>

              {/* Payment methods */}
              <div
                className="border border-white/10 bg-zinc-900 rounded-xl p-6"
                data-testid="payment-methods-card"
              >
                <div className="text-[11px] uppercase tracking-[0.3em] text-zinc-500 font-mono-display mb-4">
                  Métodos de pago
                </div>
                <div className="space-y-3">
                  <PaymentToggleRow
                    icon={Landmark}
                    label="Zelle"
                    hint="Depósitos verificados vía Saldo1980"
                    enabled={zelleEnabled}
                    onToggle={(v) => togglePayment("zelle", v)}
                    disabled={savingPayments}
                    testid="toggle-zelle"
                  />
                  <PaymentToggleRow
                    icon={CreditCard}
                    label="Tarjeta (Stripe)"
                    hint="Fee 2.9% + $0.30"
                    enabled={stripeEnabled}
                    onToggle={(v) => togglePayment("stripe", v)}
                    disabled={savingPayments}
                    testid="toggle-stripe"
                  />
                </div>
                {!zelleEnabled && !stripeEnabled ? (
                  <div
                    data-testid="sales-closed-warning"
                    className="mt-4 border border-red-500/40 bg-red-500/10 rounded-lg px-3 py-2.5"
                  >
                    <div className="text-red-300 text-xs font-bold uppercase tracking-widest mb-1">
                      ⛔ Ventas cerradas
                    </div>
                    <p className="text-[11px] text-red-200/90 leading-relaxed">
                      Los clientes verán un aviso en el portal y no podrán crear nuevos pedidos.
                      Reactiva al menos un método cuando vuelva la disponibilidad.
                    </p>
                  </div>
                ) : (
                  <p className="text-[11px] text-zinc-500 mt-4 leading-relaxed">
                    Al desactivar un método, los clientes ya no podrán elegirlo al hacer un pedido nuevo.
                    Si desactivas ambos, las ventas quedarán cerradas hasta que reactives al menos uno.
                  </p>
                )}
              </div>

              {/* Gmail */}
              <div className="border border-white/10 bg-zinc-900 rounded-xl p-6">
                <div className="text-[11px] uppercase tracking-[0.3em] text-zinc-500 font-mono-display mb-2">
                  Integración Gmail / Saldo1980 (detección automática Zelle)
                </div>

                {gmail.saldo_configured ? (
                  <>
                    <div className="flex items-center gap-2 text-emerald-400 mb-3">
                      <MailCheck className="w-4 h-4" />
                      <span className="font-medium text-sm">
                        Conectado vía Saldo1980 API
                      </span>
                    </div>
                    <p className="text-xs text-zinc-500 mb-3 break-all">
                      Fuente: <code className="text-yellow-300">{gmail.saldo_url}</code>
                    </p>
                    <Button
                      onClick={scanZelle}
                      disabled={scanning}
                      data-testid="saldo-scan-now-btn"
                      className="bg-yellow-400 hover:bg-yellow-300 text-black font-bold"
                    >
                      <Search className={`w-4 h-4 mr-1.5 ${scanning ? "animate-spin" : ""}`} />
                      Escanear Zelle
                    </Button>
                    <Button
                      onClick={scanStripe}
                      disabled={scanning}
                      data-testid="saldo-scan-stripe-btn"
                      className="ml-2 bg-indigo-500 hover:bg-indigo-400 text-white font-bold"
                    >
                      <CreditCard className={`w-4 h-4 mr-1.5 ${scanning ? "animate-spin" : ""}`} />
                      Escanear Stripe
                    </Button>
                  </>
                ) : !gmail.credentials_configured && !gmail.imap_configured ? (
                  <>
                    <div className="flex items-center gap-2 text-amber-400 mb-3">
                      <MailX className="w-4 h-4" />
                      <span className="font-medium text-sm">Gmail no configurado</span>
                    </div>
                    <p className="text-sm text-zinc-400 mb-4">
                      Configura <code className="text-yellow-300">GMAIL_IMAP_EMAIL</code> y{" "}
                      <code className="text-yellow-300">GMAIL_IMAP_PASSWORD</code> (App Password) en el backend.
                    </p>
                  </>
                ) : gmail.imap_configured ? (
                  <>
                    <div className="flex items-center gap-2 text-emerald-400 mb-3">
                      <MailCheck className="w-4 h-4" />
                      <span className="font-medium text-sm">Conectado vía App Password como {gmail.email}</span>
                    </div>
                    <p className="text-xs text-zinc-500 mb-3">
                      El sistema escanea este inbox cada 30s buscando pagos Zelle y auto-confirma
                      cuando el nombre + monto coincide con una orden pendiente.
                    </p>
                    <Button
                      onClick={scanZelle}
                      disabled={scanning}
                      data-testid="gmail-scan-now-btn"
                      className="bg-yellow-400 hover:bg-yellow-300 text-black font-bold"
                    >
                      <Search className={`w-4 h-4 mr-1.5 ${scanning ? "animate-spin" : ""}`} />
                      Escanear Zelle
                    </Button>
                    <Button
                      onClick={scanStripe}
                      disabled={scanning}
                      data-testid="stripe-scan-now-btn"
                      className="ml-2 bg-indigo-500 hover:bg-indigo-400 text-white font-bold"
                    >
                      <CreditCard className={`w-4 h-4 mr-1.5 ${scanning ? "animate-spin" : ""}`} />
                      Escanear Stripe
                    </Button>
                  </>
                ) : gmail.connected ? (
                  <>
                    <div className="flex items-center gap-2 text-emerald-400 mb-3">
                      <MailCheck className="w-4 h-4" />
                      <span className="font-medium text-sm">Conectado (OAuth) como {gmail.email}</span>
                    </div>
                    <Button
                      variant="outline"
                      onClick={disconnectGmail}
                      data-testid="gmail-disconnect-btn"
                      className="border-white/10 bg-transparent text-zinc-300 hover:bg-red-500/10 hover:text-red-400"
                    >
                      <MailX className="w-4 h-4 mr-1.5" /> Desconectar
                    </Button>
                  </>
                ) : (
                  <>
                    <div className="flex items-center gap-2 text-zinc-400 mb-3">
                      <Mail className="w-4 h-4" />
                      <span className="font-medium text-sm">No conectado</span>
                    </div>
                    <Button
                      onClick={connectGmail}
                      data-testid="gmail-connect-btn"
                      className="bg-yellow-400 hover:bg-yellow-300 text-black font-bold"
                    >
                      <Mail className="w-4 h-4 mr-1.5" /> Conectar Gmail
                    </Button>
                  </>
                )}
              </div>
            </div>
          </TabsContent>

          {/* SECURITY EVENTS */}
          <TabsContent value="security" className="space-y-4" data-testid="security-tab">
            <SecurityEventsPanel />
          </TabsContent>
        </Tabs>
      </main>

      <OrderDetailDialog
        order={detailOrder}
        onClose={() => setDetailOrder(null)}
        onConfirm={(id) => { confirmOrder(id); setDetailOrder(null); }}
        onCancel={(id) => { cancelOrder(id); setDetailOrder(null); }}
        onDelete={deleteOrder}
        onVerifyStripe={(id) => { verifyStripe(id); setDetailOrder(null); }}
        onResendEmail={resendCustomerEmail}
      />

      <CustomerEditDialog
        customer={editingCustomer}
        onClose={() => setEditingCustomer(null)}
        onSave={saveCustomer}
      />

      <CustomerHistoryDialog
        customer={historyCustomer}
        onClose={() => setHistoryCustomer(null)}
      />
    </div>
  );
}

function FuelBreakdownCard({ row }) {
  const testid = `fuel-breakdown-${row.key}`;
  return (
    <div
      className="border border-white/10 bg-zinc-900 rounded-lg p-4"
      data-testid={testid}
    >
      <div className="text-[10px] uppercase tracking-widest font-mono-display text-yellow-300/80 mb-2 truncate">
        {row.label}
      </div>
      <div className="font-display font-black text-2xl text-white" data-testid={`${testid}-total`}>
        ${row.total.toFixed(2)}
      </div>
      <div className="mt-2 flex items-baseline gap-2 text-[11px] font-mono-display text-zinc-500">
        <span>
          <span className="text-zinc-300" data-testid={`${testid}-liters`}>{row.liters.toFixed(2)}</span> L
        </span>
        <span>·</span>
        <span>
          <span className="text-zinc-300" data-testid={`${testid}-count`}>{row.count}</span> pago{row.count === 1 ? "" : "s"}
        </span>
      </div>
    </div>
  );
}


function AccountingCard({ icon: Icon, color, label, count, total, liters, fees, highlight = false, testid }) {
  return (
    <div
      className={`border ${highlight ? "border-yellow-400/40 bg-yellow-400/5" : "border-white/10 bg-zinc-900"} rounded-xl p-5`}
      data-testid={testid}
    >
      <div className="flex items-center gap-2 mb-3">
        <Icon className={`w-4 h-4 ${color}`} />
        <span className="text-[11px] uppercase tracking-[0.3em] font-mono-display text-zinc-500">
          {label}
        </span>
      </div>
      <div className={`font-display font-black text-4xl ${highlight ? "text-yellow-400" : "text-white"}`} data-testid={`${testid}-total`}>
        ${total.toFixed(2)}
      </div>
      <div className="mt-3 grid grid-cols-3 gap-2 text-xs">
        <div>
          <div className="text-zinc-500 uppercase tracking-widest text-[10px] font-mono-display">Pagos</div>
          <div className="text-white font-mono-display" data-testid={`${testid}-count`}>{count}</div>
        </div>
        <div>
          <div className="text-zinc-500 uppercase tracking-widest text-[10px] font-mono-display">Litros</div>
          <div className="text-white font-mono-display">{liters.toFixed(2)}</div>
        </div>
        <div>
          <div className="text-zinc-500 uppercase tracking-widest text-[10px] font-mono-display">Fees</div>
          <div className="text-white font-mono-display">${fees.toFixed(2)}</div>
        </div>
      </div>
    </div>
  );
}

function AccountingSection({ title, icon: Icon, color, orders, emptyText, testid, onRowClick, showFee = false, dateLabel = "" }) {
  const totalAmount = orders.reduce((s, o) => s + (o.total || 0), 0);
  const totalLiters = orders.reduce((s, o) => s + (o.liters ?? o.gallons ?? 0), 0);
  const totalFees = orders.reduce((s, o) => s + (o.fee || 0), 0);
  const sorted = [...orders].sort((a, b) => new Date(b.created_at || 0) - new Date(a.created_at || 0));

  const buildRows = () => sorted.map((o) => [
    o.created_at ? new Date(o.created_at).toLocaleString("es-ES", { day: "2-digit", month: "2-digit", year: "numeric", hour: "2-digit", minute: "2-digit" }) : "—",
    o.name || "",
    o.email || "",
    o.phone || "",
    o.code || "—",
    o.fuel_label ? (o.fuel_octane ? `${o.fuel_label} (${o.fuel_octane})` : o.fuel_label) : "Gasolina B94",
    String(o.liters ?? o.gallons ?? 0),
    `$${(o.price_per_liter ?? o.price_per_gallon ?? 0).toFixed(2)}`,
    `$${(o.fee || 0).toFixed(2)}`,
    `$${(o.total || 0).toFixed(2)}`,
    o.payment_method === "stripe" ? "Tarjeta" : "Zelle",
    o.status || "",
  ]);
  const csvHeaders = ["Fecha", "Cliente", "Email", "Teléfono", "Código", "Combustible", "Litros", "Precio/L", "Fee", "Total", "Método", "Estado"];

  const exportCsv = () => {
    const escape = (v) => `"${String(v).replace(/"/g, '""')}"`;
    const rows = [csvHeaders, ...buildRows()];
    rows.push([]);
    rows.push(["", "", "", "", "SUBTOTAL", "", totalLiters.toFixed(2), `Ø $${totalLiters > 0 ? (totalAmount / totalLiters).toFixed(2) : "0.00"}`, `$${totalFees.toFixed(2)}`, `$${totalAmount.toFixed(2)}`, "", ""]);
    const csv = rows.map((r) => r.map(escape).join(",")).join("\n");
    const blob = new Blob(["\uFEFF" + csv], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    const slug = title.toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-|-$/g, "");
    a.href = url;
    a.download = `contabilidad-${slug}-${new Date().toISOString().slice(0, 10)}.csv`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  const exportPdf = () => {
    const doc = new jsPDF({ orientation: "landscape", unit: "pt", format: "letter" });
    doc.setFontSize(16);
    doc.setTextColor(20, 20, 20);
    doc.text("Portal Gasolina B94 — Contabilidad", 40, 40);
    doc.setFontSize(11);
    doc.setTextColor(90, 90, 90);
    doc.text(title, 40, 60);
    doc.setFontSize(9);
    const subtitle = [
      dateLabel ? `Rango: ${dateLabel}` : null,
      `Generado: ${new Date().toLocaleString("es-ES")}`,
      `Pagos: ${orders.length}`,
    ].filter(Boolean).join("   ·   ");
    doc.text(subtitle, 40, 76);

    autoTable(doc, {
      startY: 92,
      head: [csvHeaders],
      body: buildRows(),
      styles: { fontSize: 8, cellPadding: 4 },
      headStyles: { fillColor: [25, 25, 25], textColor: [250, 204, 21], fontStyle: "bold" },
      alternateRowStyles: { fillColor: [248, 248, 248] },
      foot: [[
        "", "", "", "", "SUBTOTAL",
        totalLiters.toFixed(2),
        `Ø $${totalLiters > 0 ? (totalAmount / totalLiters).toFixed(2) : "0.00"}`,
        `$${totalFees.toFixed(2)}`,
        `$${totalAmount.toFixed(2)}`,
        "", "",
      ]],
      footStyles: { fillColor: [235, 235, 235], textColor: [0, 0, 0], fontStyle: "bold" },
    });

    const slug = title.toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-|-$/g, "");
    doc.save(`contabilidad-${slug}-${new Date().toISOString().slice(0, 10)}.pdf`);
  };

  return (
    <div className="border border-white/10 bg-zinc-900 rounded-xl overflow-x-auto" data-testid={testid}>
      <div className="flex flex-wrap items-center gap-2 px-5 py-3 border-b border-white/10 bg-black/20">
        <Icon className={`w-4 h-4 ${color}`} />
        <h3 className="font-display font-bold">{title}</h3>
        <span className="ml-1 text-xs text-zinc-500 font-mono-display">({orders.length})</span>
        <div className="flex-1" />
        <Button
          size="sm"
          variant="outline"
          onClick={exportCsv}
          disabled={orders.length === 0}
          data-testid={`${testid}-export-csv`}
          className="border-white/10 bg-transparent text-zinc-300 hover:bg-white/5 h-8"
        >
          <FileDown className="w-3.5 h-3.5 mr-1.5" /> CSV
        </Button>
        <Button
          size="sm"
          variant="outline"
          onClick={exportPdf}
          disabled={orders.length === 0}
          data-testid={`${testid}-export-pdf`}
          className="border-white/10 bg-transparent text-zinc-300 hover:bg-white/5 h-8"
        >
          <FileText className="w-3.5 h-3.5 mr-1.5" /> PDF
        </Button>
      </div>
      <Table>
        <TableHeader>
          <TableRow className="border-white/10 hover:bg-transparent">
            <TableHead className="text-zinc-500 uppercase text-xs tracking-widest">Fecha</TableHead>
            <TableHead className="text-zinc-500 uppercase text-xs tracking-widest">Cliente</TableHead>
            <TableHead className="text-zinc-500 uppercase text-xs tracking-widest">Código</TableHead>
            <TableHead className="text-zinc-500 uppercase text-xs tracking-widest">Litros</TableHead>
            <TableHead className="text-zinc-500 uppercase text-xs tracking-widest">Precio/L</TableHead>
            {showFee && <TableHead className="text-zinc-500 uppercase text-xs tracking-widest">Fee</TableHead>}
            <TableHead className="text-zinc-500 uppercase text-xs tracking-widest">Total</TableHead>
            <TableHead className="text-zinc-500 uppercase text-xs tracking-widest">Estado</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {sorted.length === 0 && (
            <TableRow className="border-white/5">
              <TableCell colSpan={showFee ? 8 : 7} className="text-center text-zinc-500 py-8">
                {emptyText}
              </TableCell>
            </TableRow>
          )}
          {sorted.map((o) => (
            <TableRow
              key={o.id}
              onClick={() => onRowClick?.(o)}
              className="border-white/5 cursor-pointer hover:bg-white/[0.02]"
              data-testid={`${testid}-row-${o.id}`}
            >
              <TableCell className="text-xs text-zinc-400">
                {o.created_at ? new Date(o.created_at).toLocaleString("es-ES", { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" }) : "—"}
              </TableCell>
              <TableCell className="font-medium text-sm">
                {o.name}
                <div className="text-xs text-zinc-500">{o.email}</div>
              </TableCell>
              <TableCell className="font-mono-display text-yellow-300 tracking-widest">{o.code || "—"}</TableCell>
              <TableCell className="font-mono-display">{o.liters ?? o.gallons}</TableCell>
              <TableCell className="font-mono-display text-zinc-300 text-sm">
                ${(o.price_per_liter ?? o.price_per_gallon ?? 0).toFixed(2)}
              </TableCell>
              {showFee && (
                <TableCell className="font-mono-display text-zinc-400 text-xs">
                  ${(o.fee || 0).toFixed(2)}
                </TableCell>
              )}
              <TableCell className="font-mono-display text-yellow-400 font-bold">
                ${o.total.toFixed(2)}
                {o.fuel_label && (
                  <div className="text-[10px] text-yellow-200/70 font-normal font-mono-display uppercase tracking-widest">
                    {o.fuel_label}
                  </div>
                )}
              </TableCell>
              <TableCell><StatusBadge status={o.status} /></TableCell>
            </TableRow>
          ))}
          {sorted.length > 0 && (
            <TableRow className="border-white/10 bg-black/30 font-bold">
              <TableCell colSpan={3} className="text-right text-zinc-400 text-xs uppercase tracking-widest">Subtotal</TableCell>
              <TableCell className="font-mono-display" data-testid={`${testid}-subtotal-liters`}>{totalLiters.toFixed(2)}</TableCell>
              <TableCell className="font-mono-display text-zinc-500 text-xs" data-testid={`${testid}-avg-price`}>
                Ø ${totalLiters > 0 ? (totalAmount / totalLiters).toFixed(2) : "0.00"}
              </TableCell>
              {showFee && (
                <TableCell className="font-mono-display text-zinc-400" data-testid={`${testid}-subtotal-fees`}>
                  ${totalFees.toFixed(2)}
                </TableCell>
              )}
              <TableCell className="font-mono-display text-yellow-400 text-lg" data-testid={`${testid}-subtotal-total`}>
                ${totalAmount.toFixed(2)}
              </TableCell>
              <TableCell />
            </TableRow>
          )}
        </TableBody>
      </Table>
    </div>
  );
}

function StatCard({ label, value, icon: Icon, color, testid }) {
  return (
    <div className="border border-white/10 bg-zinc-900 rounded-xl p-4" data-testid={testid}>
      <div className="flex items-center justify-between mb-2">
        <span className="text-[10px] uppercase tracking-[0.25em] text-zinc-500 font-mono-display">
          {label}
        </span>
        <Icon className={`w-3.5 h-3.5 ${color}`} />
      </div>
      <div className={`font-display font-black text-2xl md:text-3xl ${color}`}>{value}</div>
    </div>
  );
}

function statusLabel(s) {
  return { pending: "Pendiente", confirmed: "Confirmada", dispatched: "Despachada", cancelled: "Cancelada" }[s] || s;
}

function StatusBadge({ status, editable = false, onChange }) {
  const styles = {
    pending: "bg-amber-500/20 text-amber-300 border-amber-500/30",
    confirmed: "bg-emerald-500/20 text-emerald-300 border-emerald-500/30",
    dispatched: "bg-blue-500/20 text-blue-300 border-blue-500/30",
    cancelled: "bg-zinc-700 text-zinc-400 border-zinc-600",
  };
  const options = ["pending", "confirmed", "dispatched", "cancelled"];
  if (!editable) {
    return <Badge className={styles[status] || ""}>{statusLabel(status)}</Badge>;
  }
  return (
    <Popover>
      <PopoverTrigger asChild onClick={(e) => e.stopPropagation()}>
        <button
          data-testid={`status-badge-editable-${status}`}
          className={`inline-flex items-center gap-1 rounded-md border px-2 py-0.5 text-xs font-semibold hover:brightness-125 focus:outline-none focus:ring-2 focus:ring-yellow-400/40 transition ${styles[status] || ""}`}
          title="Cambiar estado"
        >
          {statusLabel(status)}
          <svg width="10" height="10" viewBox="0 0 20 20" fill="currentColor" className="opacity-70">
            <path d="M5.23 7.21a.75.75 0 011.06.02L10 11.06l3.71-3.83a.75.75 0 111.08 1.04l-4.25 4.39a.75.75 0 01-1.08 0L5.21 8.27a.75.75 0 01.02-1.06z" />
          </svg>
        </button>
      </PopoverTrigger>
      <PopoverContent
        align="start"
        onClick={(e) => e.stopPropagation()}
        className="w-48 bg-zinc-900 border-white/10 p-1"
        data-testid="status-popover"
      >
        <div className="text-[10px] uppercase tracking-widest text-zinc-500 font-mono-display px-2 py-1.5">
          Cambiar estado
        </div>
        {options.map((s) => (
          <button
            key={s}
            disabled={s === status}
            onClick={() => onChange?.(s)}
            data-testid={`status-option-${s}`}
            className={`w-full flex items-center justify-between gap-2 rounded px-2 py-1.5 text-xs text-left transition ${
              s === status
                ? "bg-white/5 text-zinc-500 cursor-default"
                : "text-white hover:bg-white/10"
            }`}
          >
            <span>{statusLabel(s)}</span>
            <span className={`w-1.5 h-1.5 rounded-full ${
              s === "pending" ? "bg-amber-400"
              : s === "confirmed" ? "bg-emerald-400"
              : s === "dispatched" ? "bg-blue-400"
              : "bg-zinc-500"
            }`} />
          </button>
        ))}
      </PopoverContent>
    </Popover>
  );
}

function PaymentBadge({ method }) {
  const isStripe = method === "stripe";
  return (
    <Badge
      className={
        isStripe
          ? "bg-violet-500/20 text-violet-300 border-violet-500/30"
          : "bg-cyan-500/20 text-cyan-300 border-cyan-500/30"
      }
    >
      {isStripe ? "Tarjeta" : "Zelle"}
    </Badge>
  );
}

function CreateWorkerDialog({ onCreated }) {
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ name: "", email: "", password: "" });
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await api.post("/admin/workers", form);
      toast.success("Trabajador creado");
      setOpen(false);
      setForm({ name: "", email: "", password: "" });
      onCreated?.();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Error");
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button className="bg-yellow-400 hover:bg-yellow-300 text-black font-bold" data-testid="create-worker-btn">
          <UserPlus className="w-4 h-4 mr-1.5" /> Nuevo trabajador
        </Button>
      </DialogTrigger>
      <DialogContent className="bg-zinc-900 border-white/10 text-white">
        <DialogHeader>
          <DialogTitle className="font-display text-2xl">Crear trabajador</DialogTitle>
        </DialogHeader>
        <form onSubmit={submit} className="space-y-4" data-testid="worker-create-form">
          <div className="space-y-1.5">
            <Label className="text-xs uppercase tracking-widest text-zinc-400 font-mono-display">Nombre</Label>
            <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} required data-testid="worker-name-input" className="bg-zinc-950 border-white/10 text-white" />
          </div>
          <div className="space-y-1.5">
            <Label className="text-xs uppercase tracking-widest text-zinc-400 font-mono-display">Email</Label>
            <Input type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} required data-testid="worker-email-input" className="bg-zinc-950 border-white/10 text-white" />
          </div>
          <div className="space-y-1.5">
            <Label className="text-xs uppercase tracking-widest text-zinc-400 font-mono-display">Contraseña</Label>
            <Input type="password" value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} required data-testid="worker-password-input" className="bg-zinc-950 border-white/10 text-white" />
          </div>
          <Button type="submit" disabled={loading} className="w-full bg-yellow-400 hover:bg-yellow-300 text-black font-bold" data-testid="worker-submit-btn">
            {loading ? "Creando..." : "Crear trabajador"}
          </Button>
        </form>
      </DialogContent>
    </Dialog>
  );
}

function CustomerEditDialog({ customer, onClose, onSave }) {
  const [form, setForm] = useState({ name: "", email: "", active: true });
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (customer) {
      setForm({ name: customer.name || "", email: customer.email || "", active: !!customer.active });
    }
  }, [customer]);

  if (!customer) return null;

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    await onSave({ name: form.name, email: form.email, active: form.active });
    setSaving(false);
  };

  return (
    <Dialog open={!!customer} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="bg-zinc-900 border-white/10 text-white" data-testid="customer-edit-dialog">
        <DialogHeader>
          <DialogTitle className="font-display text-2xl">Editar cliente</DialogTitle>
          <DialogDescription className="text-zinc-400">
            {customer.orders_count} pedido{customer.orders_count === 1 ? "" : "s"} · ${(customer.total_spent || 0).toFixed(2)} gastados
          </DialogDescription>
        </DialogHeader>
        <form onSubmit={submit} className="space-y-4">
          <div className="space-y-1.5">
            <Label className="text-xs uppercase tracking-widest text-zinc-400 font-mono-display">Nombre</Label>
            <Input
              value={form.name}
              onChange={(e) => setForm({ ...form, name: e.target.value })}
              required
              data-testid="customer-edit-name"
              className="bg-zinc-950 border-white/10 text-white"
            />
          </div>
          <div className="space-y-1.5">
            <Label className="text-xs uppercase tracking-widest text-zinc-400 font-mono-display">Email</Label>
            <Input
              type="email"
              value={form.email}
              onChange={(e) => setForm({ ...form, email: e.target.value })}
              required
              data-testid="customer-edit-email"
              className="bg-zinc-950 border-white/10 text-white"
            />
          </div>
          <label className="flex items-center gap-2.5 rounded-md border border-white/10 bg-zinc-950 px-3 py-2.5 cursor-pointer">
            <input
              type="checkbox"
              checked={form.active}
              onChange={(e) => setForm({ ...form, active: e.target.checked })}
              data-testid="customer-edit-active"
              className="h-4 w-4 accent-yellow-400 cursor-pointer"
            />
            <span className="text-sm text-zinc-300">Cuenta activa</span>
          </label>
          <div className="flex gap-2 justify-end pt-2">
            <Button
              type="button"
              variant="outline"
              onClick={onClose}
              className="border-white/10 bg-transparent text-zinc-300 hover:bg-white/5"
            >
              Cancelar
            </Button>
            <Button
              type="submit"
              disabled={saving}
              data-testid="customer-edit-save"
              className="bg-yellow-400 hover:bg-yellow-300 text-black font-bold"
            >
              {saving ? "Guardando..." : "Guardar cambios"}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}


function CustomerHistoryDialog({ customer, onClose }) {
  const [orders, setOrders] = useState(null);

  useEffect(() => {
    if (!customer) { setOrders(null); return; }
    api.get(`/admin/customers/${customer.id}/orders`)
      .then((r) => setOrders(r.data))
      .catch(() => setOrders([]));
  }, [customer]);

  if (!customer) return null;

  return (
    <Dialog open={!!customer} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="bg-zinc-900 border-white/10 text-white max-w-4xl" data-testid="customer-history-dialog">
        <DialogHeader>
          <DialogTitle className="font-display text-2xl">Pedidos de {customer.name}</DialogTitle>
          <DialogDescription className="text-zinc-400">
            {customer.email} · {customer.orders_count} pedidos · ${(customer.total_spent || 0).toFixed(2)} gastados
          </DialogDescription>
        </DialogHeader>
        {orders === null ? (
          <div className="py-10 text-center text-zinc-500"><Loader2 className="w-6 h-6 animate-spin mx-auto text-yellow-400" /></div>
        ) : orders.length === 0 ? (
          <div className="py-10 text-center text-zinc-500">Este cliente aún no tiene pedidos.</div>
        ) : (
          <div className="max-h-[60vh] overflow-y-auto border border-white/10 rounded-lg">
            <Table>
              <TableHeader>
                <TableRow className="border-white/10 hover:bg-transparent">
                  <TableHead className="text-zinc-500 uppercase text-xs tracking-widest">Fecha</TableHead>
                  <TableHead className="text-zinc-500 uppercase text-xs tracking-widest">Litros</TableHead>
                  <TableHead className="text-zinc-500 uppercase text-xs tracking-widest">Pago</TableHead>
                  <TableHead className="text-zinc-500 uppercase text-xs tracking-widest">Total</TableHead>
                  <TableHead className="text-zinc-500 uppercase text-xs tracking-widest">Código</TableHead>
                  <TableHead className="text-zinc-500 uppercase text-xs tracking-widest">Estado</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {orders.map((o) => (
                  <TableRow key={o.id} className="border-white/5" data-testid={`customer-history-row-${o.id}`}>
                    <TableCell className="text-xs text-zinc-300">
                      {o.created_at ? new Date(o.created_at).toLocaleString("es-ES", { day: "2-digit", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" }) : "—"}
                    </TableCell>
                    <TableCell className="font-mono-display">{o.liters ?? o.gallons}</TableCell>
                    <TableCell><PaymentBadge method={o.payment_method} /></TableCell>
                    <TableCell className="font-mono-display font-bold text-yellow-400">
                      ${o.total.toFixed(2)}
                      {o.fuel_label && (
                        <div className="text-[10px] text-yellow-200/70 font-normal font-mono-display uppercase tracking-widest">
                          {o.fuel_label}
                        </div>
                      )}
                    </TableCell>
                    <TableCell className="font-mono-display text-yellow-300 tracking-widest">{o.code || "—"}</TableCell>
                    <TableCell><StatusBadge status={o.status} /></TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}


function OrderDetailDialog({ order, onClose, onConfirm, onCancel, onDelete, onVerifyStripe, onResendEmail }) {
  const [confirmDelete, setConfirmDelete] = useState(false);
  if (!order) return null;

  const created = new Date(order.created_at).toLocaleString("es-ES");
  const confirmedAt = order.confirmed_at ? new Date(order.confirmed_at).toLocaleString("es-ES") : null;
  const dispatchedAt = order.dispatched_at ? new Date(order.dispatched_at).toLocaleString("es-ES") : null;

  return (
    <Dialog open={!!order} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="bg-zinc-900 border-white/10 text-white max-w-2xl" data-testid="order-detail-dialog">
        <DialogHeader>
          <DialogTitle className="font-display text-2xl flex items-center justify-between">
            <span>Detalle de la orden</span>
            <StatusBadge status={order.status} />
          </DialogTitle>
          <DialogDescription className="text-zinc-500 font-mono-display text-xs tracking-widest">
            #{order.id.slice(0, 8).toUpperCase()}
          </DialogDescription>
        </DialogHeader>

        <div className="grid grid-cols-2 gap-4 py-2">
          {order.zelle_duplicate_review && (
            <div
              className="col-span-2 border border-red-500/50 bg-red-500/10 rounded-md p-3"
              data-testid="detail-dup-alert"
            >
              <div className="flex items-start gap-2">
                <AlertTriangle className="w-5 h-5 text-red-300 shrink-0 mt-0.5" />
                <div className="flex-1">
                  <div className="text-red-200 font-bold text-sm mb-1">
                    Depósito Zelle duplicado — revisión manual necesaria
                  </div>
                  <div className="text-xs text-red-100/80 leading-relaxed">
                    Este pago (<span className="font-mono">{order.zelle_duplicate_deposit_id}</span>) ya fue usado por otra
                    orden{" "}
                    {order.zelle_duplicate_of && (
                      <>
                        (
                        <span className="font-mono text-red-200">
                          #{order.zelle_duplicate_of.slice(0, 8).toUpperCase()}
                        </span>
                        )
                      </>
                    )}
                    . Confírmala manualmente sólo si es un cobro legítimo distinto, o cancélala.
                  </div>
                </div>
              </div>
            </div>
          )}
          <DetailField label="Cliente" value={order.name} />
          <DetailField label="Email" value={order.email} />
          <DetailField label="Teléfono" value={order.phone} />
          <DetailField
            label="Combustible"
            value={
              order.fuel_label
                ? `${order.fuel_label}${order.fuel_octane ? " · " + order.fuel_octane : ""}`
                : "Gasolina B94"
            }
          />
          <DetailField label="Litros" value={(order.liters ?? order.gallons ?? 0).toString()} />
          <DetailField label="Precio/litro" value={`$${(order.price_per_liter ?? order.price_per_gallon ?? 0).toFixed(2)}`} />
          <DetailField label="Subtotal" value={`$${(order.subtotal ?? order.total).toFixed(2)}`} />
          <DetailField label="Fee" value={`$${(order.fee ?? 0).toFixed(2)}`} />
          <DetailField label="Total" value={`$${order.total.toFixed(2)}`} highlight />
          <DetailField label="Método" value={order.payment_method === "stripe" ? "Tarjeta (Stripe)" : "Zelle"} />
          <DetailField label="Zelle a" value={order.zelle_email} />
          <DetailField label="Creada" value={created} />
          {confirmedAt && <DetailField label="Confirmada" value={confirmedAt} />}
          {dispatchedAt && <DetailField label="Despachada" value={dispatchedAt} />}
          {order.dispatched_by && <DetailField label="Despachada por" value={order.dispatched_by} />}
          {order.code && (
            <div className="col-span-2 border border-yellow-400/30 bg-yellow-400/5 rounded-md p-4 text-center">
              <div className="text-[11px] uppercase tracking-[0.3em] text-yellow-400 font-mono-display mb-1">
                Código de despacho
              </div>
              <div className="font-mono-display font-black text-4xl tracking-[0.25em] text-yellow-400 code-glow">
                {order.code}
              </div>
            </div>
          )}
          {order.payment_note && (
            <div className="col-span-2 text-xs text-zinc-500 italic">
              Nota: {order.payment_note}
            </div>
          )}
          {order.zelle_receipt_url && (
            <div className="col-span-2 border border-emerald-500/30 bg-emerald-500/5 rounded-md p-3">
              <div className="text-[11px] uppercase tracking-[0.25em] text-emerald-300 font-mono-display mb-2 flex items-center gap-1.5">
                <ImageIcon className="w-3 h-3" /> Recibo Zelle enviado por el cliente
              </div>
              <a
                href={`${process.env.REACT_APP_BACKEND_URL}${order.zelle_receipt_url}`}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="detail-view-receipt-link"
                className="block"
              >
                <img
                  src={`${process.env.REACT_APP_BACKEND_URL}${order.zelle_receipt_url}`}
                  alt="Recibo Zelle"
                  className="w-full max-h-80 object-contain rounded bg-black/40 hover:opacity-90 transition-opacity cursor-zoom-in"
                  data-testid="detail-receipt-image"
                />
              </a>
            </div>
          )}
        </div>

        <div className="flex flex-wrap gap-2 pt-4 border-t border-white/5">
          {onResendEmail && order.email && (order.status === "pending" || order.status === "confirmed" || order.status === "dispatched") && (
            <Button
              variant="outline"
              onClick={() => onResendEmail(order.id)}
              data-testid="detail-resend-email-btn"
              className="border-white/10 bg-transparent text-zinc-300 hover:bg-yellow-400/10 hover:text-yellow-300 hover:border-yellow-400/30"
            >
              <Mail className="w-4 h-4 mr-1.5" /> Reenviar email al cliente
            </Button>
          )}
          {order.status === "pending" && (
            <>
              {order.payment_method === "stripe" && onVerifyStripe && (
                <Button
                  onClick={() => onVerifyStripe(order.id)}
                  data-testid="detail-verify-stripe-btn"
                  className="bg-indigo-500 hover:bg-indigo-400 text-white font-bold"
                >
                  <CreditCard className="w-4 h-4 mr-1.5" /> Verificar Stripe
                </Button>
              )}
              <Button
                onClick={() => onConfirm(order.id)}
                data-testid="detail-confirm-btn"
                className="bg-emerald-500 hover:bg-emerald-400 text-white font-bold"
              >
                <CheckCircle2 className="w-4 h-4 mr-1.5" /> Confirmar pago
              </Button>
              <Button
                variant="outline"
                onClick={() => onCancel(order.id)}
                data-testid="detail-cancel-btn"
                className="border-white/10 bg-transparent text-zinc-300 hover:bg-red-500/10 hover:text-red-400"
              >
                <XCircle className="w-4 h-4 mr-1.5" /> Cancelar orden
              </Button>
            </>
          )}
          <div className="flex-1" />
          {!confirmDelete ? (
            <Button
              variant="ghost"
              onClick={() => setConfirmDelete(true)}
              data-testid="detail-delete-btn"
              className="text-red-400 hover:text-red-300 hover:bg-red-500/10"
            >
              <Trash2 className="w-4 h-4 mr-1.5" /> Eliminar
            </Button>
          ) : (
            <div className="flex items-center gap-2">
              <span className="text-xs text-zinc-400">¿Seguro?</span>
              <Button
                size="sm"
                onClick={() => onDelete(order.id)}
                data-testid="detail-delete-confirm-btn"
                className="bg-red-500 hover:bg-red-400 text-white"
              >
                Sí, eliminar
              </Button>
              <Button
                size="sm"
                variant="ghost"
                onClick={() => setConfirmDelete(false)}
                className="text-zinc-400"
              >
                No
              </Button>
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}

function DetailField({ label, value, highlight = false }) {
  return (
    <div>
      <div className="text-[11px] uppercase tracking-[0.25em] text-zinc-500 font-mono-display mb-1">
        {label}
      </div>
      <div className={`text-sm break-words ${highlight ? "text-yellow-400 font-bold text-base" : "text-white"}`}>
        {value}
      </div>
    </div>
  );
}


function ChangePasswordDialog({ worker }) {
  const [open, setOpen] = useState(false);
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (password.length < 4) {
      toast.error("Mínimo 4 caracteres");
      return;
    }
    setLoading(true);
    try {
      await api.patch(`/admin/workers/${worker.id}/password`, { password });
      toast.success(`Contraseña actualizada para ${worker.name}`);
      setPassword("");
      setOpen(false);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Error al actualizar");
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button
          size="sm"
          variant="outline"
          className="h-8 border-white/10 bg-transparent hover:bg-yellow-400/10 hover:text-yellow-300 hover:border-yellow-400/30"
          data-testid={`change-password-worker-${worker.id}`}
        >
          <KeyRound className="w-3.5 h-3.5 mr-1" /> Contraseña
        </Button>
      </DialogTrigger>
      <DialogContent className="bg-zinc-900 border-white/10 text-white">
        <DialogHeader>
          <DialogTitle className="font-display text-2xl">Cambiar contraseña</DialogTitle>
          <DialogDescription className="text-zinc-400">
            Trabajador: <b className="text-white">{worker.name}</b> ({worker.email})
          </DialogDescription>
        </DialogHeader>
        <form onSubmit={submit} className="space-y-4" data-testid="change-password-form">
          <div className="space-y-1.5">
            <Label className="text-xs uppercase tracking-widest text-zinc-400 font-mono-display">
              Nueva contraseña
            </Label>
            <Input
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder="Mínimo 4 caracteres"
              autoFocus
              required
              data-testid="new-password-input"
              className="bg-zinc-950 border-white/10 text-white focus-visible:ring-yellow-400 focus-visible:border-yellow-400"
            />
            <p className="text-xs text-zinc-500 mt-1">
              El trabajador debe usar esta nueva contraseña para iniciar sesión.
            </p>
          </div>
          <Button
            type="submit"
            disabled={loading}
            data-testid="submit-new-password-btn"
            className="w-full bg-yellow-400 hover:bg-yellow-300 text-black font-bold h-11"
          >
            <KeyRound className="w-4 h-4 mr-1.5" />
            {loading ? "Guardando..." : "Actualizar contraseña"}
          </Button>
        </form>
      </DialogContent>
    </Dialog>
  );
}


function PaymentToggleRow({ icon: Icon, label, hint, enabled, onToggle, disabled, testid }) {
  return (
    <div
      className={`flex items-center justify-between rounded-lg border px-4 py-3 transition-colors ${
        enabled ? "border-emerald-500/30 bg-emerald-500/5" : "border-white/10 bg-zinc-950"
      }`}
      data-testid={testid}
    >
      <div className="flex items-center gap-3">
        <div
          className={`w-9 h-9 rounded-md flex items-center justify-center ${
            enabled ? "bg-emerald-500/20 text-emerald-300" : "bg-zinc-800 text-zinc-500"
          }`}
        >
          <Icon className="w-4 h-4" />
        </div>
        <div>
          <div className="font-display font-bold text-white text-sm flex items-center gap-2">
            {label}
            <span
              className={`text-[10px] uppercase tracking-widest font-mono-display px-1.5 py-0.5 rounded ${
                enabled ? "bg-emerald-500/20 text-emerald-300" : "bg-zinc-800 text-zinc-500"
              }`}
              data-testid={`${testid}-status`}
            >
              {enabled ? "Activo" : "Inactivo"}
            </span>
          </div>
          <div className="text-xs text-zinc-500 mt-0.5">{hint}</div>
        </div>
      </div>
      <Switch
        checked={enabled}
        onCheckedChange={onToggle}
        disabled={disabled}
        data-testid={`${testid}-switch`}
        className="data-[state=checked]:bg-emerald-500 data-[state=unchecked]:bg-zinc-700"
      />
    </div>
  );
}

function SecurityEventsPanel() {
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [filterType, setFilterType] = useState("all");
  const [filterEmail, setFilterEmail] = useState("");
  const [skip, setSkip] = useState(0);
  const LIMIT = 50;

  const load = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ limit: String(LIMIT), skip: String(skip) });
      if (filterType && filterType !== "all") params.set("event_type", filterType);
      if (filterEmail.trim()) params.set("email", filterEmail.trim());
      const { data } = await api.get(`/admin/security-events?${params.toString()}`);
      setItems(data.items || []);
      setTotal(data.total || 0);
    } catch (err) {
      toast.error("Error al cargar eventos de seguridad");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, [skip, filterType]);

  const eventBadge = (t) => {
    const map = {
      login_success: { label: "Login OK", cls: "bg-emerald-500/20 text-emerald-300 border-emerald-500/30" },
      lockout: { label: "Bloqueo", cls: "bg-red-500/20 text-red-300 border-red-500/30" },
      password_changed: { label: "Password cambiado", cls: "bg-yellow-400/20 text-yellow-300 border-yellow-400/30" },
      password_reset_requested: { label: "Reset solicitado", cls: "bg-blue-500/20 text-blue-300 border-blue-500/30" },
    };
    const cfg = map[t] || { label: t, cls: "bg-zinc-700 text-zinc-300 border-zinc-600" };
    return <Badge className={`${cfg.cls} border font-mono text-[10px]`}>{cfg.label}</Badge>;
  };

  const fmt = (iso) => {
    if (!iso) return "-";
    try {
      const d = new Date(iso);
      return d.toLocaleString("es", { dateStyle: "short", timeStyle: "medium" });
    } catch { return iso; }
  };

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <h2 className="font-display font-bold text-2xl flex items-center gap-2">
          <Shield className="w-6 h-6 text-yellow-400" />
          Eventos de seguridad
        </h2>
        <div className="text-sm text-zinc-400" data-testid="security-events-count">
          {total} eventos totales
        </div>
      </div>

      <div className="flex flex-wrap items-center gap-2">
        <Select value={filterType} onValueChange={(v) => { setFilterType(v); setSkip(0); }}>
          <SelectTrigger className="w-56 bg-zinc-900 border-white/10" data-testid="security-type-filter">
            <SelectValue placeholder="Tipo de evento" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Todos los eventos</SelectItem>
            <SelectItem value="login_success">Login exitoso</SelectItem>
            <SelectItem value="lockout">Bloqueos</SelectItem>
            <SelectItem value="password_changed">Contraseña cambiada</SelectItem>
            <SelectItem value="password_reset_requested">Reset solicitado</SelectItem>
          </SelectContent>
        </Select>
        <Input
          placeholder="Filtrar por email"
          value={filterEmail}
          onChange={(e) => setFilterEmail(e.target.value)}
          onKeyDown={(e) => { if (e.key === "Enter") { setSkip(0); load(); } }}
          className="max-w-xs bg-zinc-900 border-white/10 text-white h-9"
          data-testid="security-email-filter"
        />
        <Button
          onClick={() => { setSkip(0); load(); }}
          disabled={loading}
          size="sm"
          className="bg-yellow-400 hover:bg-yellow-300 text-black font-bold"
          data-testid="security-refresh-btn"
        >
          {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <RefreshCw className="w-4 h-4" />}
        </Button>
      </div>

      <div className="border border-white/10 rounded-lg overflow-hidden bg-zinc-950/40">
        <Table>
          <TableHeader>
            <TableRow className="border-white/10 hover:bg-transparent">
              <TableHead className="text-zinc-400 text-xs uppercase tracking-wider">Fecha</TableHead>
              <TableHead className="text-zinc-400 text-xs uppercase tracking-wider">Evento</TableHead>
              <TableHead className="text-zinc-400 text-xs uppercase tracking-wider">Email</TableHead>
              <TableHead className="text-zinc-400 text-xs uppercase tracking-wider">Rol</TableHead>
              <TableHead className="text-zinc-400 text-xs uppercase tracking-wider">IP</TableHead>
              <TableHead className="text-zinc-400 text-xs uppercase tracking-wider hidden md:table-cell">User agent</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody data-testid="security-events-table-body">
            {items.length === 0 && !loading && (
              <TableRow>
                <TableCell colSpan={6} className="text-center text-zinc-500 py-10">
                  <Activity className="w-8 h-8 mx-auto mb-2 opacity-40" />
                  No hay eventos de seguridad todavía. Aparecerán aquí en cuanto ocurra actividad de auth.
                </TableCell>
              </TableRow>
            )}
            {items.map((ev) => (
              <TableRow key={ev.id} className="border-white/5 hover:bg-white/[0.02]" data-testid={`security-event-${ev.id}`}>
                <TableCell className="text-xs text-zinc-300 font-mono whitespace-nowrap">{fmt(ev.created_at)}</TableCell>
                <TableCell>{eventBadge(ev.event_type)}</TableCell>
                <TableCell className="text-sm text-zinc-200">{ev.email || "-"}</TableCell>
                <TableCell className="text-xs text-zinc-400">{ev.role || "-"}</TableCell>
                <TableCell className="text-xs text-zinc-400 font-mono">{ev.ip || "-"}</TableCell>
                <TableCell className="text-xs text-zinc-500 max-w-xs truncate hidden md:table-cell" title={ev.user_agent || ""}>
                  {ev.user_agent || "-"}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>

      {total > LIMIT && (
        <div className="flex items-center justify-between text-sm text-zinc-400">
          <div>Mostrando {skip + 1}–{Math.min(skip + LIMIT, total)} de {total}</div>
          <div className="flex gap-2">
            <Button
              variant="outline"
              size="sm"
              disabled={skip === 0 || loading}
              onClick={() => setSkip(Math.max(0, skip - LIMIT))}
              data-testid="security-prev-btn"
            >
              <ChevronLeft className="w-4 h-4" />
            </Button>
            <Button
              variant="outline"
              size="sm"
              disabled={skip + LIMIT >= total || loading}
              onClick={() => setSkip(skip + LIMIT)}
              data-testid="security-next-btn"
            >
              <ChevronRight className="w-4 h-4" />
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}



function FuelTypesEditor({ initial, onSaved }) {
  const [rows, setRows] = useState(initial || []);
  const [saving, setSaving] = useState(false);

  useEffect(() => { setRows(initial || []); }, [initial]);

  const updateRow = (idx, key, val) => {
    const copy = [...rows];
    copy[idx] = { ...copy[idx], [key]: val };
    setRows(copy);
  };

  const save = async () => {
    const cleaned = rows.map((r) => ({
      key: (r.key || "").trim().toLowerCase(),
      label: (r.label || "").trim() || "Sin nombre",
      octane: (r.octane || "").trim(),
      price: Math.max(0.01, Math.round(parseFloat(r.price || 0) * 100) / 100),
      active: r.active !== false,
    }));
    if (cleaned.some((r) => !r.key)) return toast.error("Cada fila necesita un key único");
    setSaving(true);
    try {
      const { data } = await api.put("/admin/config", { fuel_types: cleaned });
      toast.success("Tipos de combustible actualizados");
      if (onSaved) onSaved(data.fuel_types || cleaned);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Error al guardar");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="space-y-3" data-testid="fuel-editor">
      <div className="text-xs text-zinc-400 leading-relaxed">
        Estos son los tipos de combustible que verá el cliente al crear una orden. Edita nombre,
        octanaje y precio libremente. Desmarca &quot;Activo&quot; para ocultar un tipo temporalmente.
      </div>
      <div className="border border-white/10 rounded-lg overflow-hidden">
        <Table>
          <TableHeader>
            <TableRow className="border-white/10 hover:bg-transparent">
              <TableHead className="text-zinc-400 text-xs uppercase tracking-wider w-24">Key</TableHead>
              <TableHead className="text-zinc-400 text-xs uppercase tracking-wider">Nombre visible</TableHead>
              <TableHead className="text-zinc-400 text-xs uppercase tracking-wider">Octanaje</TableHead>
              <TableHead className="text-zinc-400 text-xs uppercase tracking-wider w-28">Precio ($/L)</TableHead>
              <TableHead className="text-zinc-400 text-xs uppercase tracking-wider w-20">Activo</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {rows.map((r, i) => (
              <TableRow key={i} className="border-white/5" data-testid={`fuel-row-${r.key || i}`}>
                <TableCell><Input value={r.key || ""} onChange={(e) => updateRow(i, "key", e.target.value)} className="bg-zinc-950 border-white/10 text-white h-9 font-mono text-xs" data-testid={`fuel-key-${i}`} /></TableCell>
                <TableCell><Input value={r.label || ""} onChange={(e) => updateRow(i, "label", e.target.value)} className="bg-zinc-950 border-white/10 text-white h-9" data-testid={`fuel-label-${i}`} /></TableCell>
                <TableCell><Input value={r.octane || ""} onChange={(e) => updateRow(i, "octane", e.target.value)} className="bg-zinc-950 border-white/10 text-white h-9" data-testid={`fuel-octane-${i}`} /></TableCell>
                <TableCell><Input type="number" step="0.01" min="0.01" value={r.price ?? ""} onChange={(e) => updateRow(i, "price", e.target.value)} className="bg-zinc-950 border-white/10 text-white h-9 font-mono" data-testid={`fuel-price-${i}`} /></TableCell>
                <TableCell>
                  <input
                    type="checkbox"
                    checked={r.active !== false}
                    onChange={(e) => updateRow(i, "active", e.target.checked)}
                    className="w-5 h-5 accent-yellow-400 cursor-pointer"
                    data-testid={`fuel-active-${i}`}
                  />
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>
      <div className="flex justify-end">
        <Button
          onClick={save}
          disabled={saving}
          data-testid="save-fuel-types-btn"
          className="bg-yellow-400 hover:bg-yellow-300 text-black font-bold"
        >
          {saving ? "Guardando..." : "Guardar tipos de combustible"}
        </Button>
      </div>
    </div>
  );
}

